import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Space,
  TimePicker,
  Row,
  Col,
  Tag,
  Modal,
  message,
} from 'antd';
import { MinusOutlined, PlusCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import {
  LinkStatus,
  WarehouseType,
  PickupSiteFormValues,
  PickupSiteFormMode,
  AbMappingRow,
  StandingScheduleRow,
  SiteContactPerson,
} from '../types/pickupSite';
import { SAVE_COOPERATION_WAREHOUSE } from '../../cooperation-warehouse/graphql/mutations/cooperationWarehouseMutations';
// 2.2 需求点2：站点作为 A 的 AB 映射（方向与表单体内联那块相反，见 openAbWizard 上方注释）
import AbMappingWizard, { AbMappingSubmit } from './AbMappingWizard';
import {
  PREVIEW_STATION_AB_RETIRE,
  PreviewStationAbRetireData,
} from '../graphql/queries/stationAbMappingAsSource';
import {
  RESOLVE_STATION_AS_AB_MAPPING,
  ResolveStationAbMappingInput,
  ResolveStationAsAbMappingResponse,
} from '../graphql/mutations/resolveStationAsAbMapping';
import { GET_ALL_CHANNEL_LITE, allChannelLiteResponse } from '../../../../graphql/queries/getChannelAll';
import { extractDock } from '../hooks/useAddressStandardize';
import PickupAddressFields, { PickupAddressValue, StandardizedAddress } from './PickupAddressFields';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
import StationFollowUpTimeline from './StationFollowUpTimeline';
import { usePickupSiteV2Gray } from '../../../../hooks/usePickupSiteV2Gray';
import { VEHICLE_TYPE_STATUS_ENABLED } from '../../../../graphql/queries/getAllVehicleTypes';
import { getWeekdaysOptions } from '../../appointment/types/PickupAppointmentType';
import {
  GET_STATION_AB_MAPPINGS,
  GetStationAbMappingsResponse,
  GetStationAbMappingsVariables,
} from '../graphql/queries/getStationAbMappings';
import {
  GET_STATION_ASSOCIATED_ADDRESSES,
  GetStationAssociatedAddressesResponse,
  GetStationAssociatedAddressesVariables,
} from '../graphql/queries/getStationAssociatedAddresses';
import {
  SAVE_STATION_AB_MAPPINGS,
  SaveStationAbMappingsResponse,
  SaveStationAbMappingsInput,
} from '../graphql/mutations/saveStationAbMappings';
import { translateGraphQLError } from '../../../../utils/graphqlUtils';
import {
  PREVIEW_STATION_AB_MAPPING_CONFLICTS,
  PreviewStationAbMappingConflictsResponse,
  PreviewStationAbMappingConflictsVariables,
  AbMappingConflict,
} from '../graphql/mutations/previewStationAbMappingConflicts';
import {
  SUBMIT_SECOND_CONTACT,
  SecondContactInput,
  SubmitSecondContactResponse,
} from '../graphql/mutations/submitSecondContact';
import { MODAL_HEADER_STYLE, MODAL_FOOTER_STYLE, modalBodyStyle, modalTitle } from '../utils/modalChrome';
import { isContactPhoneTaken } from '../utils/contactPhoneDedup';

/** 保存时透传给 saveCooperationWarehouse 的拆分地址（由后端内部重标准化）。 */
export interface SiteAddressForSave {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  /**
   * 该预填地址是否已标准化成功。审核池系统进单里存在「标准化失败」的地址（diagnosisType=StandardizeFailed），
   * 首次打开就应显示为未标准化、强制重新标准化后再保存。缺省（undefined）视为已标准化（编辑/查看既有站点、AB 向导等入口）。
   */
  standardized?: boolean;
}

// 单串地址按逗号拆四段。**输入框已全部改为分段录入，此函数仅用于解析后端返回的 displayAddress**
// （AB 向导自指判定的兜底路径）——它自带"街道含逗号即拆错"的缺陷，不要再用于用户输入。
export const parseFullAddress = (
  full: string,
): { street: string; city: string; state: string; zip: string } | null => {
  const parts = (full || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length < 3) return null;
  const street = parts[0];
  if (parts.length >= 4) {
    return { street, city: parts[1], state: parts[2], zip: parts[parts.length - 1] };
  }
  const tail = parts[2].split(/\s+/).filter(Boolean);
  if (tail.length < 2) return null;
  return { street, city: parts[1], state: tail.slice(0, -1).join(' '), zip: tail[tail.length - 1] };
};

interface Props {
  open: boolean;
  mode: PickupSiteFormMode;
  initialValues?: Partial<PickupSiteFormValues>;
  /** 状态是否可改（审核池入口按联系结果预置且不可改 → false） */
  statusEditable?: boolean;
  onClose: () => void;
  /** mock 回退（未提供 warehouseId 时走此路径，仅返回表单值不落库） */
  onSubmit?: (values: PickupSiteFormValues) => void;
  /** 真实保存所需：站点所属 hub。提供后 handleOk 走 saveCooperationWarehouse。 */
  warehouseId?: number;
  /**
   * 站点所属 hub，**仅用于 2.0 灰度判定，不参与保存**。
   *
   * <p>为什么不能复用 [warehouseId]：那个 prop 的语义是「要不要落库」——`warehouseId == null`
   * 是走「值回传不落库」路径的开关，所以只读场景调用方会刻意传 undefined
   * （见 useStationViewer 的 `warehouseId={mode === 'view' ? undefined : hubId}`）。
   * 灰度判定若只读 warehouseId，查看态就恒不命中，而底部跟进记录本就只在查看态出现 ⇒ 该功能全废。
   */
  stationHubId?: number;
  /** 编辑模式的既有站点 id（更新站点时传，创建不传）。 */
  cooperationWarehouseId?: number;
  /**
   * 是否显示左下角「AB地址映射」入口（2.2 需求点2），**默认关**。
   *
   * <p>本表单被三处以 edit 模式打开：站点管理列表、司机反馈跟进详情、审核池历史存量「更新站点」。
   * PRD 原型只画了**站点管理**那一处，故默认不显示、由调用方显式开启 —— 不默认全开是因为：
   * 审核池那个页面本身已有一个「AB映射」动作（条目当 A），同屏两个 AB 入口而 A 的含义相反，
   * 会把运营搞糊涂。要扩散到其它入口等产品明确提出。
   */
  showAbEntry?: boolean;
  /**
   * AB 映射成功后的回调（2.2 需求点2），调用方用它刷新列表。
   *
   * <p><b>刻意不复用 [onSaved]</b>：那条通道在 useStationViewer 里挂的是 handleSaved，带着
   * 拆分上下文结算与 onSavedOnce（二次建联转已建联）两类副作用 —— AB 映射不是「站点被保存了」，
   * 误触发会把一个待处理的二次建联回调提前消费掉。
   *
   * <p>不接这个回调的后果：站点可能已被整站退役，而列表里它还在，运营能再点进去。
   */
  onAbMapped?: () => void;
  /**
   * 来源审核池条目 id（2.2 需求点1）。
   *
   * <p><b>把站点保存成「确认无法建联(5)」时必传</b>：后端 saveCooperationWarehouse 会拿它向 pickup
   * 校验准入（诊断为标准化失败，或已上报地址异常），不通过直接拒绝建站 —— 因为 link5 会让该地址
   * 静默停止揽收（新单建出即不可见）且状态机没有回退，这道闸只能在写库之前把住。
   * 不传而又要置 5 会被后端拒绝。其它状态不需要它。
   */
  reviewPoolId?: number;
  /** 保存用拆分地址（审核池条目已标准化的地址；saveCooperationWarehouse 内部会重标准化）。 */
  addressForSave?: SiteAddressForSave;
  /**
   * 2.1 需求点1：跳过地址库重新标准化，直接用条目原始地址 + 条目自带的 hash 建站。
   *
   * <p>仅用于「审核池条目诊断=标准化失败 → 两次电联均未联系到 → 建站/更新站」场景。为 true 时：
   * ① 隐藏「地址标准化」按钮（点了也没用，该地址就是标准化不了的）；
   * ② 放行"提交前必须已标准化"的两道校验；
   * ③ 保存时带上 `skipAddressStandardize` + 两个 hash，后端不调地址库、直接采用入参。
   *
   * <p>为什么要复用条目 hash 而不是重算：条目的 core_hash 由地址库兜底分支按原始地址算出，
   * 与卡单同源；重算会因"下次调用地址库可能识别成功"产生 hash 漂移，导致同地址订单再次卡池。
   */
  skipStandardize?: boolean;
  /** 跳过标准化时必传：条目的 core_hash（命中键）。 */
  presetAddressCoreHash?: string;
  /** 跳过标准化时必传：条目的 analysis_hash（标准化失败场景与 core_hash 同值）。 */
  presetAddressAnalysisHash?: string;
  /** 保存成功回调，回传创建/更新后的合作仓 id（供上层做 resolve 回填解卡）。 */
  onSaved?: (cooperationWarehouseId: number) => void;
  /** 拆分站点（#5）：edit 模式点「拆分站点」触发，由上层（useStationViewer）编排选址+建站+迁移。 */
  onSplit?: () => void;
  /**
   * 保存时跳过「命中相同/相似站点」查重（首次即以 submitCount=2 提交，等价于自动「仍要新建」）。
   * 拆分场景专用：拆出的地址本就是源站的关联地址，查重必然命中源站，是误报；此处直接放行。
   */
  skipDedupOnSave?: boolean;
  /**
   * 站点在**库里的**建联状态（`cw.link_status` 原值），与表单里可被预置覆盖的 `initialValues.linkStatus` 区分开。
   *
   * <p>为什么要单独一个字段：二次建联「更新站点」入口会把状态框**预置显示**为「已建联」——那是保存后的
   * 结果，不是当前事实。而「是否要走二次建联流转」必须按**保存前**的真实状态判断（2/3 才转），
   * 若沿用被覆盖后的表单值（4），`doSecondContact` 恒为 false → 不调状态机、不写 op6，
   * 而 saveCooperationWarehouse 只写群字段并保留原 link_status → 界面显示已建联、库里仍是待建联，静默不一致。
   *
   * <p>缺省（未传）时回落到表单值：审核池建站 / AB 向导等入口本就没有"预置覆盖"，两者等价。
   */
  originalLinkStatus?: number;
}

export interface PickupSiteFormBodyRef {
  submit: () => void;
}
type PickupSiteFormBodyProps = Omit<Props, 'onClose'>;

/**
 * AB 行是否还有"填了地址但没点标准化"的。
 *
 * <p>AB 行改为「单行完整地址 + 标准化回填四段」后，四段只由标准化写入：运营填了地址却没标准化，
 * persistAbMappings 会因 addressLine1 为空把整行**静默丢弃**。故提示与提交拦截共用这一份判据，
 * 避免两处各写一遍后出现"提示常亮但不拦"或"拦了却没提示"。
 */
/** AB 行 → 提交用 item，提交与基线比对共用同一口径（避免两处归一化不一致产生假变更）。 */
const toAbItems = (rows?: AbMappingRow[]) =>
  (rows || [])
    .filter((r) => r.addressLine1 && r.addressLine1.trim())
    .map((r) => ({
      originAddressLine1: r.addressLine1!.trim(),
      originAddressLine2: r.addressLine2 ? r.addressLine2.trim() : null,
      originCity: r.city ? r.city.trim() : '',
      originState: r.state ? r.state.trim() : '',
      originZipcode: r.zipcode ? r.zipcode.trim() : '',
      contactName: r.contactName ? r.contactName.trim() : null,
      contactPhone: r.contactPhone ? r.contactPhone.trim() : null,
      channelId: r.channelId != null ? Number(r.channelId) : null,
    }));

/**
 * 两个 AB 集合是否等价。按序列化后排序比对——集合语义下**顺序无意义**，
 * 运营在表单里上下拖动一行不该被当成"改了映射"。
 */
const abItemsEqual = (a: ReturnType<typeof toAbItems>, b: ReturnType<typeof toAbItems>): boolean => {
  if (a.length !== b.length) return false;
  const norm = (xs: ReturnType<typeof toAbItems>) => xs.map((x) => JSON.stringify(x)).sort();
  const na = norm(a);
  const nb = norm(b);
  return na.every((v, i) => v === nb[i]);
};

const abRowsNeedStandardize = (rows?: AbMappingRow[]): boolean =>
  (rows || []).some(
    (r) => !!r?.addrFields?.street && `${r.addrFields.street}`.trim() !== '' && !r.addrFields?.standardized,
  );

/**
 * 有「填了地址但联系方式或客户渠道为空」的 AB 行 —— 命中键不全，落库即死数据。
 *
 * AB 命中前置要求 core_hash + 联系方式 + 客户渠道**三者非空**才查映射表
 * （`OrderCooperationWarehouseBindingService.resolveExpress2`），且 `findActiveMatch` 对
 * phone/channel 用 `=` 比较、NULL 恒不等。⇒ 缺任一维的行两头都命不中，而保存是成功的、
 * 运营完全无感知。生产实测 17 条 active 映射里 3 条(18%)如此，**全部来自本表单入口**。
 *
 * 判据以「地址已填」为前提，与 [abRowsNeedStandardize] 一致：空白新增行不该被拦，
 * 否则运营点了「+」还没填就提交不了。后端 `saveStationAbMappings` 有同口径兜底跳过。
 */
const abRowsMissingHitKey = (rows?: AbMappingRow[]): boolean =>
  (rows || []).some((r) => {
    const hasAddress = !!r?.addrFields?.street && `${r.addrFields.street}`.trim() !== '';
    if (!hasAddress) return false;
    return !r?.contactPhone || `${r.contactPhone}`.trim() === '' || r?.channelId == null;
  });

/**
 * 联系人默认展示条数（PRD 2.2 需求点4：「联系人默认仅展示3条数据，超过3条时展示查看更多联系人操作」）。
 * 超出的行**不渲染**，只能通过「查看更多联系人」的只读弹窗查看 —— 这是 PRD 配图的形态。
 */
const CONTACT_VISIBLE_LIMIT = 3;

/**
 * 联系方式重复时是否「只提示、不阻断保存」。
 *
 * **2026-10-16 改为硬拦（false）**：产品定「运营如果填，也不能联系方式重复」，
 * 且上线前会先用 `fixDedupCooperationWarehouseContacts` 把生产存量清洗一遍。
 *
 * <p><b>为什么此前是软提示</b>（保留这段，解释清楚翻开关的前提，别让下一个人以为可以随便翻回去）：
 * 2026-10-15 实测，灰度三个 hub 下 5490 个在范围站点里有 **2544 个（46%）** 命中重复。
 * antd 在提交时会校验**全部**行（不只用户改过的那行），所以存量没清干净就开硬拦，
 * 这些站点连改个工作时间都保存不了 —— 等于近一半站点的编辑功能中断。
 *
 * <p>⚠️ **部署前提**（缺一不可，否则运营会被卡住）：
 * 1. 生产存量已跑过 `fixDedupCooperationWarehouseContacts` 且 dryRun 复验为 0 条
 * 2. 后端 `pickup.contact.phone_dedup.enabled` 已打开 ——
 *    否则订单回填 / 自动建站 / 站点合并仍在源源不断写入重复，运营清一次卡一次
 *
 * 真出问题要急停：翻回 true 需要前端发版（本开关是编译期常量，刻意不做成运行时配置 ——
 * 做成运行时配置就意味着校验口径能在线上被改成与后端不一致）。
 */
const PHONE_DUPLICATE_WARNING_ONLY = false;

/**
 * 站点表单主体（去 Modal 壳）：创建/编辑/查看/合并/AB 向导共用（对齐原型 buildSiteForm 共享）。
 * 保存/校验逻辑全在此；成功保存后调 onSaved(cwId)、值回传路径调 onSubmit(values)，均**不自关闭**（交壳/向导决定）。
 * 通过 ref 暴露 submit() 供外层 footer 触发。
 */
export const PickupSiteFormBody = forwardRef<PickupSiteFormBodyRef, PickupSiteFormBodyProps>(({
  open,
  mode,
  initialValues,
  statusEditable = true,
  onSubmit,
  warehouseId,
  stationHubId,
  cooperationWarehouseId,
  addressForSave,
  skipStandardize = false,
  presetAddressCoreHash,
  reviewPoolId,
  presetAddressAnalysisHash,
  onSaved,
  onSplit,
  skipDedupOnSave,
  originalLinkStatus,
}, ref) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<PickupSiteFormValues>();
  const readOnly = mode === 'view';

  // 真实下拉数据（替换原 MOCK_CLIENTS / MOCK_VEHICLES）
  const { data: channelData } = useQuery<allChannelLiteResponse>(GET_ALL_CHANNEL_LITE);
  const clientOptions = useMemo(
    () => (channelData?.getAllChannelsLite ?? []).map((c) => ({ label: c.channelName, value: Number(c.id) })),
    [channelData],
  );
  // 站点级车型：口径为 vehicle_type（vehicle_model 已废弃）
  // getAllVehicleTypes 返回的 id 是 GraphQL String（VehicleTypeType.id: String?），
  // 而表单里 vehicleTypeId 走 Number；Select 按 === 匹配 option.value，不归一会匹配不上，
  // 直接把裸 id（如 6）当文案渲染出来
  /**
   * 车型下拉选项。
   *
   * <p><b>编辑/新建只出启用中的车型</b>（产品定案 2026-08-26：不展示禁用车型，也不做"已停用"标注）。
   * 与站点管理表单 {@code CooperationWarehouseFormDrawer} 的 vehicleModelActiveOptions 同口径。
   *
   * <p><b>查看模式必须用全量</b>：站点存的车型可能事后被停用，只给启用集会让历史车型显示成空白
   * 或原始 id，运营看不出这个站点原来配的是什么车。
   *
   * <p>value 归一成 Number：后端返回的 id 可能是字符串，而表单里 vehicleTypeId 走 Number，
   * Select 按 === 匹配 option.value，不归一会匹配不上、显示空白。
   */
  // 2.0 灰度门：本文件里两处 2.1 改动（车型只出启用中的、底部跟进记录）都只对命中 hub 生效，
  // 未命中的 hub 逐字节保持 2.1 之前的行为。
  //
  // ⚠️ 不能只看 warehouseId：它在 view 模式下是刻意的 undefined（不落库开关），只读它会让
  // 查看态恒不命中灰度、底部跟进记录 100% 显示不出来。故回退到 stationHubId（见其 doc）。
  const { express2Enabled } = usePickupSiteV2Gray();
  const v2Enabled = express2Enabled(warehouseId ?? stationHubId);

  const { vehicleTypes: allVehicleTypes } = useVehicleTypes();
  const vehicleTypeOptions = React.useMemo(
    () =>
      (allVehicleTypes ?? [])
        .filter((vt) => !v2Enabled || readOnly || vt.status === VEHICLE_TYPE_STATUS_ENABLED)
        .map((vt) => ({ label: vt.nameEn, value: Number(vt.id) })),
    // v2Enabled 必须在依赖里：灰度名单是异步查询（cache-first），首次挂载时先 false 后 true。
    // 漏掉它 ⇒ 灰度查询回来得比车型列表晚时 memo 不重算，编辑态仍能选到已停用车型。
    [allVehicleTypes, readOnly, v2Enabled],
  );


  const [saveWarehouse] = useMutation(SAVE_COOPERATION_WAREHOUSE, { fetchPolicy: 'no-cache' });
  // 二次建联：待建联站在编辑表单填群 → 走状态机转已建联(link4)+op6，复用司机反馈跟进同一后端
  const [submitSecondContactMutation] = useMutation<SubmitSecondContactResponse, { input: SecondContactInput }>(
    SUBMIT_SECOND_CONTACT,
  );
  // #6/G6：站点内联 AB 映射 加载 + 保存（replace 语义，saveCooperationWarehouse 成功后跟一刀）
  // 结果取 hook 的 data（与下方 relateAddresses 同一写法），不用 execute 返回的 Promise：
  // Apollo 3.8 的 useLazyQuery，execute 在「同一组件实例内重复调用」时返回的 promise 可能 resolve 出
  // data=undefined，表现为首次打开能显示、之后查看/编辑切换都空白，只有刷新页面（组件重挂载）才恢复。
  const [loadAbMappings, { data: abMappingData }] = useLazyQuery<
    GetStationAbMappingsResponse,
    GetStationAbMappingsVariables
  >(GET_STATION_AB_MAPPINGS, { fetchPolicy: 'network-only' });
  const [previewAbConflictsMutation] = useMutation<
    PreviewStationAbMappingConflictsResponse,
    PreviewStationAbMappingConflictsVariables
  >(PREVIEW_STATION_AB_MAPPING_CONFLICTS, { fetchPolicy: 'no-cache' });
  const [saveAbMappingsMutation] = useMutation<
    SaveStationAbMappingsResponse,
    { input: SaveStationAbMappingsInput }
  >(SAVE_STATION_AB_MAPPINGS);
  // 保存用揽收地址（四段）：prop/initialValues 播种，标准化按钮更新；buildSaveData 读它（不再直接读 prop）。
  const [addressState, setAddressState] = useState<SiteAddressForSave | undefined>(addressForSave);

  /**
   * 是否展开全部联系人行。两种情况会置 true：
   * ① 运营点「+」新增联系人——新行排在第 4 位以后就会被折叠掉，加了看不见等于没加；
   * ② 打开表单时发现**第 4 条及以后有不完整的联系人**（历史脏数据）。
   *    不渲染的行其 required 规则不会执行（validateFields 只校验已注册字段），
   *    但 buildSaveData 取的是 store 全量，所以脏值照样会被带去保存；而运营既看不见也改不了。
   *    自动展开是为了不把人锁死——这不是功能，是兜底。
   */
  const [showAllContacts, setShowAllContacts] = useState(false);
  /** 「查看更多联系人」弹窗是否打开。弹窗内渲染第 4 条及以后的**可编辑**行（产品 2026-10-13 定） */
  const [contactsModalOpen, setContactsModalOpen] = useState(false);

  /**
   * 被折叠的联系人里有没有「缺姓名或缺电话」的。
   *
   * 折叠的行不渲染 ⇒ 它们的 required 规则不执行，而 buildSaveData 取的是 store 全量，
   * 脏值照样会被带去保存。命中就把列表内联展开，让它们重新进入校验、运营也看得见。
   * 两个时机都要查：**打开表单时**（存量脏数据）、**关闭弹窗时**（运营刚在弹窗里改空的）。
   */
  const hiddenContactsIncomplete = (rows?: SiteContactPerson[]): boolean =>
    (rows ?? []).slice(CONTACT_VISIBLE_LIMIT).some((c) => !c?.name?.trim() || !c?.phone?.trim());

  /** 关闭「查看更多」弹窗；若运营刚把某个隐藏行改成不完整，就内联展开全部，别让它漏过校验 */
  const closeContactsModal = () => {
    setContactsModalOpen(false);
    if (hiddenContactsIncomplete(form.getFieldValue('contactPersons') as SiteContactPerson[])) {
      setShowAllContacts(true);
    }
  };

  const linkStatus = Form.useWatch('linkStatus', form);
  /**
   * 「跳过地址标准化」是否**真的**生效 = prop 说它属于豁免类别 **且** 表单当前状态仍是「确认无法建联(5)」。
   *
   * <p>prop（`skipStandardize`）由调用方按**库里**的状态算，表达的是"这个站点属于免标准化类别"。
   * 但运营在本抽屉里把状态从「确认无法建联」改成「已建联」，意味着他联系上客户了、接下来会
   * **重新填写客户给的地址** —— 那份新地址必须走一次标准化校验。此时要把「地址标准化」按钮放出来、
   * 保存门禁重新生效，否则新地址会带着旧站点的 hash 原样落库，命中键直接错。
   *
   * <p>`linkStatus == null` 视为仍豁免：`Form.useWatch` 首帧早于 setFieldsValue 落值，
   * 那一瞬按"不豁免"处理会让提示与按钮闪一下。以 prop（库里真相）为准更稳。
   */
  const skipStandardizeEffective =
    skipStandardize && (linkStatus == null || linkStatus === LinkStatus.Unreachable);
  const standingPickup = Form.useWatch('standingPickup', form);
  // AB 提示与提交拦截共用 abRowsNeedStandardize：两处判据分开写过一次，
  // 结果提示常亮而拦截不触发（或反之），运营只能看着警告猜要不要点确定。
  const abMappingRows = Form.useWatch('abMappings', form) as AbMappingRow[] | undefined;
  const abNeedsStandardize = abRowsNeedStandardize(abMappingRows);
  // 关联地址（只读展示）：取 relate 表（address_source_type=2 = 被并入本站点的地址），含完整四段。
  // 不用 initialValues.relateAddresses —— 那份来自 1.0 的 related_address_json，结构只有 address1/address2，
  // 没有 city/state/zipcode，展示出来只剩街道号，运营看不出是哪个地址。
  const [loadRelateAddresses, { data: relateAddressData }] = useLazyQuery<
    GetStationAssociatedAddressesResponse,
    GetStationAssociatedAddressesVariables
  >(GET_STATION_ASSOCIATED_ADDRESSES, { fetchPolicy: 'network-only' });
  const relateAddresses = relateAddressData?.getStationAssociatedAddresses;
  /** 已加载的 AB 行缓存：供 form.resetFields() 之后回填（见下方 effect 注释）。 */
  const loadedAbRowsRef = useRef<AbMappingRow[]>([]);
  /**
   * AB 映射集是否已成功取回。
   *
   * saveStationAbMappings 是 **replace 语义**（整站重写），而保存时无条件调用；若 AB 查询失败/超时/
   * 尚未返回，表单里的 abMappings 就是空的，replace 会把该站点已有的 AB 映射**全部静默清空**，
   * 运营毫无感知。故必须区分「运营手动删空了」与「压根没加载」——只有前者允许提交空集合。
   * 空数组也算已加载（该站点确实没有映射），所以用独立标志而非判断 rows.length。
   */
  const abLoadedRef = useRef(false);
  const requireGroup = linkStatus === LinkStatus.Linked; // 已建联必填群ID+群名
  /**
   * 站点在库里的建联状态。二次建联入口会把状态框预置显示成「已建联」（保存后的结果），
   * 所以凡是「该不该走二次建联流转」这类判断，一律读这个值，不能读被覆盖后的表单值。
   */
  const dbLinkStatus = originalLinkStatus ?? linkStatus;
  // 二次建联入口：编辑模式下「揽收成功待建联(2)/揽收失败待建联(3)」站点，展示群字段，填群保存即转已建联(link4)
  const isSecondContactEditable =
    mode === 'edit' &&
    (dbLinkStatus === LinkStatus.PickedUpPending || dbLinkStatus === LinkStatus.PickupFailedPending);
  const showGroupFields = requireGroup || isSecondContactEditable;

  useEffect(() => {
    if (open) {
      form.resetFields();
      // 审核池流从 addressForSave 播种结构化四段；编辑/查看流四段已在 initialValues(cwFormMapper)
      const addrSeed = addressForSave
        ? {
            addressLine1: addressForSave.addressLine1,
            addressLine2: addressForSave.addressLine2,
            city: addressForSave.city,
            state: addressForSave.state,
            zipcode: addressForSave.zipcode,
          }
        : {};
      form.setFieldsValue({
        // 空行默认仅用于新建/编辑录入；查看模式只用 initialValues，避免多出空排期/联系人行
        ...(readOnly
          ? {}
          : { contactPersons: [{}], standingPickup: false, standingSchedules: [newScheduleRow()] }),
        ...initialValues,
        ...addrSeed,
      });
      // 联系人折叠的兜底（见 showAllContacts 注释）：第 4 条及以后只要有缺姓名或缺电话的，
      // 一律展开全部。否则这条脏数据既看不见、也改不了，而必填校验又过不去 —— 运营会被彻底卡死。
      setShowAllContacts(
        hiddenContactsIncomplete((initialValues?.contactPersons ?? []) as SiteContactPerson[]),
      );
      setContactsModalOpen(false);
      // 保存地址 state：审核池取 addressForSave，编辑/查看取 initialValues 四段；有值即视为已标准化（审核池/既有站点地址均已标准化）
      const seeded: SiteAddressForSave | undefined =
        addressForSave ??
        (initialValues?.addressLine1 || initialValues?.city
          ? {
              addressLine1: initialValues.addressLine1,
              addressLine2: initialValues.addressLine2,
              city: initialValues.city,
              state: initialValues.state,
              zipcode: initialValues.zipcode,
              country: 'US',
            }
          : undefined);
      setAddressState(seeded);
      // 分段地址播种：把预填的 addressLine1 拆成「街道 + Dock」两段。
      // 1.0 autoCreate 建的站点 dock 内联在 addressLine1 里（如 "20-20 129th St # 3010"），
      // 2.0 表单建的站点 dock 在独立列 —— extractDock 兼容两种；dock 列有值时以它为准（运营显式录入优先）。
      // resolved.addressLine1 统一拼回 dock，把存量数据顺带归到新口径（编辑保存即修正其 core_hash）。
      if (seeded) {
        const { dock: inlineDock, stripped } = extractDock(seeded.addressLine1 ?? '');
        const seedDock = (initialValues?.dock ?? '').trim() || inlineDock;
        const street = stripped || seeded.addressLine1;
        const isStd = seeded.standardized !== false;
        form.setFieldValue('addrFields', {
          street,
          // 小号从 addressLine2 播种（仅录入/展示，不进命中）
          unit: seeded.addressLine2 ?? undefined,
          dock: seedDock,
          state: seeded.state,
          city: seeded.city,
          zipcode: seeded.zipcode,
          // 仅当显式标记为未标准化（审核池「标准化失败」条目）才为 false，首次打开即显示 ⚠ 强制重新标准化；
          // 其余入口（编辑/查看既有站点、AB 向导）缺省视为已标准化
          standardized: isStd,
          resolved: isStd
            ? { ...seeded, addressLine1: [street, seedDock].filter(Boolean).join(' ') }
            : undefined,
        } as PickupAddressValue);
      }
      // resetFields 会连同已加载的 AB 一起清掉；本 effect 在 initialValues 变化（查看↔编辑切换）时也会重跑，
      // 而 AB 加载 effect 依赖未变不会重跑，故在此用缓存回填，避免 AB 消失。
      if (loadedAbRowsRef.current.length > 0) {
        form.setFieldValue('abMappings', loadedAbRowsRef.current);
      }
    }
  }, [open, initialValues, addressForSave, form, readOnly]);

  /**
   * 排期行里已被停用的车型 → 清空该行。
   *
   * <p>不清空会有个静默 bug：选项里没有该车型时 Select 显示空，但 value 仍是旧的停用 id，
   * 运营没碰它就点确定 ⇒ 停用车型被原样存回去，而 required 规则拦不住（value 非空）。
   * 清空后必填校验会拦住提交，逼运营重选一个在用的车型。
   *
   * <p>只在可编辑态跑：查看模式本就该原样展示历史配置。
   *
   * <p><b>⚠️ 本 effect 必须声明在「初始值填充」effect 之后，位置不可上移。</b>
   * React 同一次 commit 里 effect 按**声明顺序**执行 —— 放到填充之前的话，这里
   * {@code getFieldValue('standingSchedules')} 读到的还是空值，清空逻辑整段空转，
   * 停用车型会原样留在表单里（初版就踩了这个坑）。
   *
   * <p>车型数据异步后到的场景由 {@code allVehicleTypes} 依赖兜住：打开时若尚未加载则直接
   * return，加载完成后本 effect 会因依赖变化重跑，那时表单已填充完毕。
   */
  React.useEffect(() => {
    if (!v2Enabled || readOnly || !allVehicleTypes?.length) {
      return;
    }
    const activeIds = new Set(
      allVehicleTypes.filter((vt) => vt.status === VEHICLE_TYPE_STATUS_ENABLED).map((vt) => Number(vt.id)),
    );
    const schedules = form.getFieldValue('standingSchedules');
    if (!Array.isArray(schedules) || schedules.length === 0) {
      return;
    }
    let changed = false;
    const next = schedules.map((row) => {
      if (row?.vehicleTypeId != null && !activeIds.has(Number(row.vehicleTypeId))) {
        changed = true;
        return { ...row, vehicleTypeId: undefined };
      }
      return row;
    });
    if (changed) {
      form.setFieldsValue({ standingSchedules: next });
    }
    // 依赖必须与「初始值填充」effect 的一致（open/initialValues/addressForSave/form/readOnly）：
    // 少了 initialValues 的话，同一次打开中 initialValues 变化触发重新播种时本 effect 不重跑，
    // 已停用的 vehicleTypeId 会被重新播回表单且不再被清掉。
  }, [allVehicleTypes, readOnly, form, open, initialValues, addressForSave, v2Enabled]);

  // 换站点时清缓存：否则上一个站点的 AB 会被 effect1 回填到当前站点（串站）。
  // 只依赖 cwId —— 此前挂在加载 effect 里无条件执行，导致「关闭抽屉(open→false)」也会清掉缓存，
  // 重开时 effect1 无可回填。查看↔编辑切换 cwId 不变，缓存得以保留。
  useEffect(() => {
    loadedAbRowsRef.current = [];
    // 标志必须一起重置：否则切到新站点后、其 AB 尚未返回就保存，会拿着"已加载"的错误前提清空新站点映射
    abLoadedRef.current = false;
  }, [cooperationWarehouseId]);

  // #6/G6：edit/view 打开既有站点时触发加载（AB 映射集 + 关联地址）
  useEffect(() => {
    if (open && cooperationWarehouseId != null) {
      loadRelateAddresses({ variables: { cooperationWarehouseId } });
      loadAbMappings({ variables: { cooperationWarehouseId } });
    } else if (open) {
      // 无 cwId（纯 mock/值回传路径）没有既有映射可加载，视为已加载，
      // 否则运营新填的 AB 行会被防护逻辑当成"未加载"而跳过保存
      abLoadedRef.current = true;
    }
  }, [open, cooperationWarehouseId, loadAbMappings, loadRelateAddresses]);

  // AB 数据到达 → 映射为表单行、缓存并回填。
  // 与触发分离：数据经 hook 的 data 回来（非 promise），这里只对 data 变化做响应。
  useEffect(() => {
    const list = abMappingData?.getStationAbMappings;
    if (!list) return;
    const rows: AbMappingRow[] = list.map((m) => {
      // 既有映射回显为分段值：origin_address_line_1 里可能内联着 dock（1.0 写入的），拆到 Dock 框
      const { dock: inlineDock, stripped } = extractDock(m.originAddressLine1 ?? '');
      return {
      addrFields: {
        street: stripped || (m.originAddressLine1 ?? ''),
        dock: inlineDock,
        state: m.originState ?? '',
        city: m.originCity ?? '',
        zipcode: m.originZipcode ?? '',
        standardized: true,
        resolved: {
          addressLine1: m.originAddressLine1 ?? '',
          addressLine2: m.originAddressLine2 ?? '',
          city: m.originCity ?? '',
          state: m.originState ?? '',
          zipcode: m.originZipcode ?? '',
          country: 'US',
          standardized: true,
        },
      },
      addressLine1: m.originAddressLine1 ?? '',
      addressLine2: m.originAddressLine2 ?? '',
      city: m.originCity ?? '',
      state: m.originState ?? '',
      zipcode: m.originZipcode ?? '',
      contactName: m.contactName ?? '',
      contactPhone: m.contactPhone ?? '',
      // Long 标量出参为字符串，转 number 以匹配 clientOptions 的数值 value
      channelId: m.channelId != null ? Number(m.channelId) : undefined,
      standardized: true,
      };
    });
    // 缓存一份供 effect1 在 form.resetFields() 后回填：查看↔编辑切换时 cwId 不变、
    // 本 effect 依赖(abMappingData)也不变不会重跑，全靠这份缓存把行找回来。
    loadedAbRowsRef.current = rows;
    abLoadedRef.current = true;
    form.setFieldValue('abMappings', rows);
  }, [abMappingData, form]);

  const wt = (k: string) => t(`pickup.addressReviewPool.siteForm.${k}`);
  /**
   * 长度限制走 Form 校验而非 input 的 maxLength：
   * maxLength 是浏览器原生硬截断，超出的字符根本打不进来，既没有任何提示、也让
   * rules 里的 max 永远不会触发（此前「名称」两者并存，看起来就是限制没生效）。
   * 改为 rules 后超长即标红并在字段下方给出提示，与其它校验错误表现一致。
   */
  const maxLenRule = (max: number) => ({
    max,
    message: t('pickup.addressReviewPool.siteForm.maxLength', { max }) as string,
  });

  const warehouseTypeOptions = [
    { label: t('pickup.addressReviewPool.warehouseType.commercial'), value: WarehouseType.Commercial },
    { label: t('pickup.addressReviewPool.warehouseType.home'), value: WarehouseType.Home },
    { label: t('pickup.addressReviewPool.warehouseType.customs'), value: WarehouseType.Customs },
  ];
  // 6上门中由「派单给司机 / 取消预约单」自动流转，不放进可选项——手选会造出「状态是上门中但根本没派单」的不一致。
  // 但当前值已是 6 时必须补一个 disabled 项，否则 antd Select 找不到 option 会直接显示裸数字 6。
  const statusOptions = (() => {
    const selectable = [1, 2, 3, 4, 5].map((v) => ({
      label: t(`pickup.addressReviewPool.linkStatus.${v}`),
      value: v as number,
      disabled: false,
    }));
    // 2.1 需求点3：编辑「确认无法建联(5)」站点时状态框解锁（见 useStationViewer 的 statusEditable），
    // 但只放行 5→4。其余目标态置灰——后端状态机 ALLOWED 里 CONFIRMED_UNABLE 的出向只有 LINKED，
    // 放开别的选项等于让运营改出一个后端必然拒绝的状态（或绕过状态机被裸写进库）。
    if (dbLinkStatus === LinkStatus.Unreachable) {
      return selectable.map((o) => ({
        ...o,
        disabled: o.value !== LinkStatus.Unreachable && o.value !== LinkStatus.Linked,
      }));
    }
    if (linkStatus === LinkStatus.OnsiteInProgress) {
      return [
        ...selectable,
        {
          label: t(`pickup.addressReviewPool.linkStatus.${LinkStatus.OnsiteInProgress}`),
          value: LinkStatus.OnsiteInProgress as number,
          disabled: true,
        },
      ];
    }
    return selectable;
  })();
  // 星期几选项复用预约单口径（i18n「周一~周日」，value 1~7 与本表单一致）
  const weekdayOptions = useMemo(() => getWeekdaysOptions(t), [t]);
  /**
   * 新增排期行的默认值：**只给工作日，两组时间一律留空**。
   *
   * 时间不预填是 PRD 2.2 需求点3 的明确要求：「增量数据 —— 营业时间：默认为空，新增时需要填写；
   * 期望上门时间：默认为空，新增时需要填写」。预填 09:00-18:00 会让四个必填框形同虚设 ——
   * 运营直接下一步，新建站点的营业时间就会 100% 是默认值，而需求点3 的全部价值
   * 正是要把营业时间和期望上门时间**真实地**区分开。四个框各自带 placeholder 作提示，
   * 想填常见值仍然看得到，只是必须自己点一下。
   *
   * ⚠️ **不要再加回默认值**。真要加（PRD 改口径）时请注意：TimePicker 的值是可变 dayjs，
   * 必须每次调用新建实例，共用一个会让多行互相串改；且构造只能用 `dayjs().hour(h)…`，
   * 不能用 `dayjs('09:00','HH:mm')` —— 后者依赖 customParseFormat 插件，本项目未全局注册，
   * 会得到 Invalid Date。
   *
   * 工作日的「周一~周五」默认**保留**：PRD 没要求改它，铁律「需求没要求的一律不改」。
   *
   * 本改动只影响新建（create 入口与「+」加行）；编辑存量站点由 initialValues 整体覆盖，
   * 走 cwFormMapper 的历史回显三级回落，对应 PRD「历史数据：原有的工作时间自动填充」。
   * 后端 DEFAULT_WORK_START/END_TIME（09:00/18:00）是另一回事，未动：站点没配时间时
   * 系统仍按它建单。
   */
  const newScheduleRow = () => ({
    weekdays: [1, 2, 3, 4, 5],
  });

  /** 当前所有排期行已占用的工作日；exceptIndex 用于排除自身行。 */
  const usedWeekdays = (exceptIndex?: number) => {
    const rows = (form.getFieldValue('standingSchedules') as StandingScheduleRow[] | undefined) || [];
    return new Set(rows.filter((_, i) => i !== exceptIndex).flatMap((r) => r?.weekdays || []));
  };

  /**
   * 点「+」新增行的默认值：工作日跨行互斥，默认只取还没被占用的日子。
   * 沿用 newScheduleRow 的「周一~周五」会与首行完全撞车，一点就红，故这里按剩余日收窄；
   * 周一~周五占满时给空数组，由必填规则提示用户自己选（周六/周日或先腾出日子）。
   */
  const nextScheduleRow = () => {
    const used = usedWeekdays();
    return { ...newScheduleRow(), weekdays: [1, 2, 3, 4, 5].filter((d) => !used.has(d)) };
  };

  /**
   * 工作日跨行去重校验：同一天只能出现在一行排期上。
   * 不拦的话，buildSaveData 只把首行的时间/车型回写到 expected_pickup_start/end_time 与 vehicle_type_id
   * （weekdayUnion 还会把重复日静默去重），后面几行对同一天的设置会被无声吞掉——用户看到两行、生效只有一行。
   */
  const weekdayUniqueRule = (index: number) => ({
    validator(_: unknown, value?: number[]) {
      if (!value?.length) return Promise.resolve();
      const taken = usedWeekdays(index);
      const dup = value.filter((d) => taken.has(d));
      if (!dup.length) return Promise.resolve();
      const labels = dup.map((d) => weekdayOptions.find((o) => o.value === d)?.label ?? d).join('、');
      return Promise.reject(new Error(`${wt('weekdayDuplicated')}${labels}`));
    },
  });

  /**
   * 联系方式跨行去重：同一站点下同一个联系方式只登记一个联系人（PRD 2.2 需求点4 的唯一键口径）。
   * 只在**离开输入框**时校验（rule 级 validateTrigger=onBlur）—— 边敲边红会在输入到一半、
   * 号码恰好与另一行前缀相同时误报。比较口径见 isContactPhoneTaken 的注释。
   * warningOnly 的理由见 PHONE_DUPLICATE_WARNING_ONLY。
   */
  const phoneUniqueRule = (index: number) => ({
    validateTrigger: 'onBlur',
    warningOnly: PHONE_DUPLICATE_WARNING_ONLY,
    validator(_: unknown, value?: string) {
      const rows = form.getFieldValue('contactPersons') as SiteContactPerson[] | undefined;
      return isContactPhoneTaken(rows, index, value)
        ? Promise.reject(new Error(wt('phoneDuplicated')))
        : Promise.resolve();
    },
  });

  /**
   * 工作日是跨行校验，而 antd 只重算被改动的那个字段：改好 A 行后，B 行的红字会一直挂着不消。
   * 所以任一行工作日变化后统一重算所有已填行。跳过空行是为了不让刚点「+」出来的新行立刻弹「必填」。
   */
  const handleValuesChange = (changed: Partial<PickupSiteFormValues>) => {
    // 联系方式同样是跨行校验，同样有「改好 B 行、A 行提示不消」的问题，处理方式与工作日一致。
    // 三点差异：
    // ① **跳过正在输入的那一行**——显式 validateFields 会无视 rule 上的 onBlur 跑全部规则，
    //    不跳过的话该行边敲边弹「已存在」，而 PRD 要的是离开输入框才提示。它自己等 blur。
    // ② 只重算已填号码的行，避免刚点「+」出来的空行立刻弹必填。
    // ③ **只重算当前真的挂着提示的行**。没有提示的行重算一遍结果还是没提示，纯浪费；
    //    而本页的联系人数可以很大（2026-10-15 实测灰度三 hub 内最大 196、>50 的有 13 个站点），
    //    点过「+」之后 showAllContacts 会把全部行都渲染出来，此时不筛选就是每敲一个键
    //    重算 N-1 个字段、每个字段内部又扫一遍 N 条 ⇒ O(N²)，大站点会明显卡顿。
    if (changed.contactPersons?.some((r) => r && 'phone' in r)) {
      const editingIndex = changed.contactPersons.findIndex((r) => r && 'phone' in r);
      const contacts = (form.getFieldValue('contactPersons') as SiteContactPerson[] | undefined) || [];
      // 当前挂着提示的联系方式行下标。本规则是 warningOnly ⇒ 过期提示挂在 warnings 上；
      // errors 一并看是为了 PHONE_DUPLICATE_WARNING_ONLY 翻成 false 后这里不用跟着改。
      const flaggedRows = new Set(
        form
          .getFieldsError()
          .filter((f) => f.errors.length > 0 || f.warnings.length > 0)
          .filter((f) => f.name[0] === 'contactPersons' && f.name[2] === 'phone')
          .map((f) => Number(f.name[1])),
      );
      const contactPaths = contacts
        .map((c, i) =>
          i !== editingIndex && c?.phone?.trim() && flaggedRows.has(i)
            ? ['contactPersons', i, 'phone']
            : null)
        .filter((p): p is (string | number)[] => p !== null);
      if (contactPaths.length) form.validateFields(contactPaths).catch(() => undefined);
    }
    if (!changed.standingSchedules?.some((r) => r && 'weekdays' in r)) return;
    const rows = (form.getFieldValue('standingSchedules') as StandingScheduleRow[] | undefined) || [];
    const paths = rows
      .map((r, i) => (r?.weekdays?.length ? ['standingSchedules', i, 'weekdays'] : null))
      .filter((p): p is (string | number)[] => p !== null);
    if (paths.length) form.validateFields(paths).catch(() => undefined);
  };

  /**
   * 主揽收地址标准化成功回调（标准化本体在 PickupAddressFields 内）。
   * 只负责把结果落进 addressState —— buildSaveData 读它作为保存源，其 addressLine1 已含 dock，
   * 与后端 core_hash 口径（标准化 + dock）一致。
   */
  const handleAddressStandardized = (resolved: StandardizedAddress) => {
    setAddressState((prev) => ({
      addressLine1: resolved.addressLine1,
      // 优先用地址库拆出的 line2；地址库未拆出时才回退旧值（保住编辑既有站点时用户原有的单元号）
      addressLine2: resolved.addressLine2 ?? prev?.addressLine2,
      city: resolved.city,
      state: resolved.state,
      zipcode: resolved.zipcode,
      country: 'US',
    }));
    message.success(t('pickup.addressReviewPool.modifyAddress.successTip'));
  };

  // #6/G6：AB 行标准化（与主揽收地址同一交互：单行填完整地址 → 解析 → 标准化 → 回填四段并标记）
  // 保存时后端仍会权威标准化算 coreHash；此处四段仅作前端预览与入参。
  /**
   * AB 行标准化成功回调（标准化本体在 PickupAddressFields 内）。把结果写回该行的四段 + standardized。
   * resolved.addressLine1 已含 dock —— AB 的 core_hash 必须与站点/订单同口径，否则 AB 前置命中永远匹配不上。
   */
  const handleAbStandardized = (name: number, resolved: StandardizedAddress) => {
    const row = (form.getFieldValue(['abMappings', name]) ?? {}) as AbMappingRow;
    form.setFields([
      {
        name: ['abMappings', name],
        value: {
          ...row,
          addressLine1: resolved.addressLine1,
          // 必须回填 line2：缺它会让 AB 的 coreHash 与站点/订单口径不一致，导致 AB 前置命中永远匹配不上
          addressLine2: resolved.addressLine2,
          city: resolved.city,
          state: resolved.state,
          zipcode: resolved.zipcode,
          standardized: true,
        },
      },
    ]);
    message.success(t('pickup.addressReviewPool.modifyAddress.successTip'));
  };

  // #6/G6：站点保存成功后 replace 整站 AB 映射集（仅填了 A 地址街道的行入库；后端权威标准化）
  const persistAbMappings = async (cwId: number, rows?: AbMappingRow[]): Promise<void> => {
    if (warehouseId == null) return;
    // 防静默清空：AB 集合未成功取回时，表单里的空 abMappings 不代表"运营删空了"，
    // 而 saveStationAbMappings 是 replace 语义，提交空集合会把该站点已有映射全部删掉且无任何提示。
    // 此时跳过这一刀（站点主体照常保存），并显式告知运营，避免他们以为映射已随保存更新。
    if (!abLoadedRef.current) {
      message.warning(wt('abNotLoadedSkipped'));
      return;
    }
    const items = toAbItems(rows);
    // AB 集合与打开时一致 ⇒ 跳过这一刀。
    //
    // 此前是无条件调用：只要站点保存成功就 replace 一次整站映射，运营改个名字/联系人/排期
    // （完全不碰 AB）也会把全部映射行删掉重建 —— 表膨胀、id 空转，review_pool_id 与 created_at
    // 每次被抹掉。后端已改成按 5 元组 diff，这里再挡一层：无关保存连 DB 往返都不产生。
    //
    // 基线取 loadedAbRowsRef（加载时缓存的原始行），两侧过同一个 toAbItems 归一化后比对，
    // 避免"看着一样但 trim/类型不同"导致的假变更。
    if (abItemsEqual(items, toAbItems(loadedAbRowsRef.current))) {
      return;
    }
    try {
      await saveAbMappingsMutation({
        variables: { input: { cooperationWarehouseId: cwId, warehouseId: Number(warehouseId), items } },
      });
    } catch (e: any) {
      // 后端把 BusinessException 的 messageKey 放在 GraphQL error 的 extensions 里，
      // 据此精确区分「A 与本站点为同一地址」与其它保存失败，而不是笼统提示重试。
      const sameAsStation = (e?.graphQLErrors ?? []).some(
        (ge: any) => ge?.extensions?.messageKey === 'errors.business.abAddress.sameAsStation',
      );
      if (sameAsStation) {
        message.error(wt('abSameAsStation'));
      } else {
        message.warning(t('pickup.addressReviewPool.siteForm.abSaveFailed', 'AB 映射保存失败，请重试'));
      }
    }
  };

  // TimePicker 值运行时为 dayjs；容错既接受 dayjs 也接受字符串
  const timeStr = (v: any): string | undefined => {
    if (!v) return undefined;
    return typeof v === 'string' ? v : v.format ? v.format('HH:mm') : undefined;
  };

  // PickupSiteFormValues → saveCooperationWarehouse 的 cooperationWarehouse 入参（含 2.0 字段）。
  // 地址走 addressForSave（审核池条目已标准化的拆分地址），saveCooperationWarehouse 内部会重标准化并算 hash/坐标。
  const buildSaveData = (values: PickupSiteFormValues) => {
    // 普通联系人（inBlacklist=false）
    const normalContacts = (values.contactPersons || [])
      .filter((c) => (c.name && c.name.trim()) || (c.phone && c.phone.trim()))
      .map((c) => ({
        name: c.name ? c.name.trim() : '',
        phone: c.phone ? c.phone.trim() : '',
        countryCode: Number(c.phoneCountryCode) || 1,
        extension: c.extension ? c.extension.trim() : '',
        inBlacklist: false,
      }));
    // 方案A：独立「黑名单配置」区的号码 → inBlacklist=true 的联系人塞进 contactPersonList，
    // 复用后端 processBlacklistRecords 维护 cooperation_warehouse_blacklist（零新字段）
    const blacklistAsContacts = (values.blacklistContacts || [])
      .filter((b) => b.phone && b.phone.trim())
      .map((b) => ({
        name: '',
        phone: b.phone!.trim(),
        countryCode: Number(b.phoneCountryCode) || 1,
        extension: b.extension ? b.extension.trim() : '',
        inBlacklist: true,
      }));
    const contactPersonList = [...normalContacts, ...blacklistAsContacts];
    const schedules = (values.standingSchedules || [])
      .filter((s) => (s.weekdays && s.weekdays.length) || s.startTime || s.endTime
                      || s.businessStartTime || s.businessEndTime || s.vehicleTypeId != null)
      .map((s) => ({
        weekdays: s.weekdays || [],
        startTime: timeStr(s.startTime),
        endTime: timeStr(s.endTime),
        // 营业时间（2.2 需求点3）：只落 standing_schedule_json，**不回写站点级列**。
        // work_start/end_time 与 expected_pickup_* 继续由期望上门时间（排期首行）回写 ——
        // 调度器排期未命中时读 cw.work_*，改成写营业时间就等于静默改掉那条回落路径的上门时间窗。
        businessStartTime: timeStr(s.businessStartTime),
        businessEndTime: timeStr(s.businessEndTime),
        vehicleTypeId: s.vehicleTypeId != null ? Number(s.vehicleTypeId) : undefined,
      }));
    const standing = values.standingPickup ?? false;
    // 排期块已统一为长期/非长期共用，「工作日」独立字段随之取消：
    // work_weekdays_json 改由各排期行的星期取并集写入，保持下游（预约单工作日）不受影响。
    const weekdayUnion = Array.from(new Set(schedules.flatMap((s) => s.weekdays))).sort((a, b) => a - b);
    // expected_pickup_start/end_time 与 vehicle_type_id 仍被预约单/派车链路读取，
    // 统一后这些站点级列由排期首行回写，避免改成只写 standing_schedule_json 后下游取不到值。
    const firstSchedule = schedules[0];
    return {
      ...(cooperationWarehouseId ? { id: cooperationWarehouseId } : {}),
      name: values.name ? values.name.trim() : '',
      type: 1, // 揽收
      warehouseId: Number(warehouseId),
      status: 1,
      addressLine1: addressState?.addressLine1 ?? '',
      // 小号存 addressLine2（不进命中：addressLine1/core_hash 不含它）；直接取表单字段，缺省回退标准化解析值
      addressLine2: values.addrFields?.unit?.trim() || addressState?.addressLine2 || '',
      country: addressState?.country ?? 'US',
      state: addressState?.state ?? '',
      city: addressState?.city ?? '',
      zipcode: addressState?.zipcode ?? '',
      contactPersonList,
      standingPickup: standing,
      // 备注信息 → remark；其他备注 → otherRemark（2.0 独立列，两个不同字段）
      remark: values.remark ? values.remark.trim() : '',
      otherRemark: values.otherRemark ? values.otherRemark.trim() : null,
      relatedChannelIds:
        values.relatedClientIds && values.relatedClientIds.length > 0
          ? values.relatedClientIds.map((id) => Number(id))
          : null,
      workWeekdayList: weekdayUnion.length > 0 ? weekdayUnion : null,
      // 地址库那次标准化的结论回传：值来自后端 standardizePickupAddress（前端只是搬运，不自造）。
      // 只在**本次确实标准化成功**时传 1；否则不传，后端沿用站点原值。
      //
      // 为什么必须回传：保存链路对「地址四段未变」走快路径、不重调地址库，只能沿用站点原 standardize_status。
      // 原值为 0（该列 2.0 新增、DEFAULT 0、无存量回填）时就永远写不进 1，运营会陷入
      // 「点标准化 → 保存 → 再打开又提示标准化」的死循环（实测站点 11297）。
      // 地址真的变了时后端会重算并写真实结论，此入参不参与，所以只在这一种情况下起作用。
      //
      // 判据复用门禁读的同一个值（addrFields.standardized），避免两处口径分叉：
      // link5 免标准化路径下它恒为 false ⇒ 不传 ⇒ 不会把标不了的地址标成已标准化。
      standardizeStatus: values.addrFields?.standardized === true ? 1 : undefined,
      // 车型写 vehicle_type_id（新口径）；use_vehicle_model_id 原样回写不改动——
      // 该列已废弃但预约单链路仍在读它派车，此处清空或改写会影响派车，待下游切换后再废弃
      vehicleTypeId: firstSchedule?.vehicleTypeId != null ? Number(firstSchedule.vehicleTypeId) : null,
      useVehicleModelId: values.useVehicleModelId ? Number(values.useVehicleModelId) : null,
      expectedPickupStartTime: firstSchedule?.startTime ?? null,
      expectedPickupEndTime: firstSchedule?.endTime ?? null,
      // work_start/end_time 与 expected_pickup_* 同源写排期首行：长期揽收预约单调度器
      // （pickup-service StandingPickupBackfillJobService）只读 work_start/end_time，
      // 不读 expected_pickup_*。此前本表单不发这两列，编辑一次即被 PUT 语义清空，
      // 预约单时间窗退化成默认 09:00-18:00。回显侧（mapCwToForm）已同样优先读 work 列，
      // 因此"打开表单不改动直接保存"是原值回写，不会静默改动上门时间。
      workStartTime: firstSchedule?.startTime ?? null,
      workEndTime: firstSchedule?.endTime ?? null,
      // channel_id：长期揽收时以「长期揽收预约客户」为准（两列同义，同时修复新建站点该列从未被赋值的问题），
      // 否则原值回写。调度器按 `channel_id is null` 硬过滤，不发该字段会让站点编辑后不再生成预约单。
      // 非长期站点保留原值即可——调度器第一道过滤是 standing_pickup，留值不会误建单。
      channelId:
        standing && values.standingClientId != null
          ? Number(values.standingClientId)
          : values.channelId
            ? Number(values.channelId)
            : null,
      // 别名：本表单现在采集了，必须回传。后端 alias_json 是**无条件写**
      // （`it[aliasJson] = aliasList?.let { toJson(it) }`，与紧邻的 relatedAddressJson 的条件写不一致），
      // 不发即写 NULL ⇒ 运营在本表单改个电话就会把别名清掉。
      aliasList: values.aliasList ?? [],
      // 经纬度：与别名同一类问题（后端对这两列同样是无条件写），本表单不展示、不编辑，只原值回写。
      // 必须有对应的隐藏 Form.Item 才取得到值 —— validateFields() 只返回**已注册字段**，
      // 未注册的 key 即使在 store 里也会被丢掉（已用 rc-field-form 2.7.1 实测）。
      // 取值写法照抄老 1.0 表单（`values.latitude ? Number(...) : null`）：隐藏 Input 承载的值
      // 理论上可能回成字符串，而 GraphQL 这两个字段是 Float，传字符串会被直接拒掉。
      latitude: values.latitude ? Number(values.latitude) : null,
      longitude: values.longitude ? Number(values.longitude) : null,
      // 本表单不采集、下游在读，原值回写防静默清空。
      // 同样依赖下方的隐藏 Form.Item 注册才取得到值（见那里的注释）。
      email: values.email ?? null,
      groupQrcodeS3Key: values.groupQrcodeS3Key ?? null,
      // ===== 2.0 字段 =====
      linkStatus: values.linkStatus ?? null,
      warehouseCategory: values.warehouseType ?? null,
      brand: values.brand ? values.brand.trim() : null,
      subBrand: values.subBrand ? values.subBrand.trim() : null,
      // dock 已并入 addrFields；addressState.addressLine1 里也含 dock（core_hash 口径），两处都要写
      dock: values.addrFields?.dock ? values.addrFields.dock.trim() : null,
      groupId: values.groupId ? values.groupId.trim() : null,
      groupName: values.groupName ? values.groupName.trim() : null,
      standingPickupChannelId: standing && values.standingClientId != null ? Number(values.standingClientId) : null,
      // 排期块长期/非长期共用，故不再按 standing 门控（此前非长期恒存 null）
      standingScheduleJson: schedules.length ? JSON.stringify(schedules) : null,
    };
  };

  /**
   * 站点保存。**自己兜住业务异常并 toast**，不再往外抛：
   *
   * 1. 本方法被 Modal.confirm 的 onOk 直接调用（「仍要新建」），antd 对 rejected 的 onOk 是
   *    「不关弹窗、不提示」，异常会彻底静默——运营点了确定后毫无响应（实测名称重复即如此）。
   * 2. 外层 handleOk 的 catch 原意只吞 form 校验异常，却把这里的业务异常一并吞了。
   *
   * 不 rethrow 是刻意的：名称重复这类错误靠再点一次「仍要新建」永远过不去，必须关掉确认弹窗
   * 让运营能回到表单改名；rethrow 会把弹窗留在前面挡住表单。
   */
  /**
   * 置「确认无法建联(5)」时随保存带上来源审核池条目 id（2.2 需求点1 闸门一）。
   *
   * <p>只在目标状态是 5 时才带：后端仅在这种情形下校验它，其余状态传了是无意义字段。
   * reviewPoolId 缺失时**不做兜底、不静默跳过** —— 后端会拒绝这次保存并给出明确错误，
   * 这正是期望行为：宁可让调用方补参数，也不能放行一个查不到出处的"让地址停收"。
   */
  const unreachableSourcePayload = (values: PickupSiteFormValues) =>
    values.linkStatus === LinkStatus.Unreachable && reviewPoolId != null
      ? { reviewPoolId }
      : {};

  /**
   * 保存前 AB 冲突预检（2.2 需求点2 · D2）。
   *
   * 返回 true = 可以继续保存（无冲突，或运营已确认）；false = 运营取消，终止整个保存。
   *
   * **必须在站点主体保存之前拦**：persistAbMappings 是站点保存成功后才跑的那一刀，
   * 到那时运营已经退不回来了 —— 弹框问「要不要失效别站映射」而唯一选项是「已经保存了」毫无意义。
   *
   * 跳过条件与 persistAbMappings 逐条对齐（AB 未加载 / AB 未变更），否则运营改个站点名也弹框，
   * 弹多了就会被无脑点确定，这道闸等于没有。
   */
  const confirmAbConflicts = async (values: PickupSiteFormValues): Promise<boolean> => {
    const cwId = cooperationWarehouseId;
    if (cwId == null || warehouseId == null) return true;
    if (!abLoadedRef.current) return true;
    const items = toAbItems(values.abMappings);
    if (abItemsEqual(items, toAbItems(loadedAbRowsRef.current))) return true;

    let conflicts: AbMappingConflict[];
    try {
      const resp = await previewAbConflictsMutation({
        variables: { input: { cooperationWarehouseId: Number(cwId), warehouseId: Number(warehouseId), items } },
      });
      conflicts = resp.data?.previewStationAbMappingConflicts ?? [];
    } catch (e: any) {
      // 预检失败**不放行**：空结果的语义是「没有冲突，放心保存」，而失败时我们根本不知道有没有。
      // 后端同样拒绝静默返回空（见 PickupStationMaintainMutation）。两头都守住，这道闸才真有用。
      message.error(
        translateGraphQLError(e, 'pickup.addressReviewPool.siteForm.abConflictPreviewFailed'),
      );
      return false;
    }
    if (conflicts.length === 0) return true;

    return new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: wt('abConflictTitle'),
        width: 640,
        okText: wt('abConflictOk'),
        okButtonProps: { danger: true },
        content: (
          <div className="flex flex-col gap-2">
            <div className="text-sm text-gray-700">{wt('abConflictDesc')}</div>
            <div className="max-h-64 overflow-auto rounded border border-gray-200">
              {conflicts.map((c) => {
                const addr = [c.originAddressLine1, c.originAddressLine2, c.originCity, c.originState, c.originZipcode]
                  .filter((x) => x && `${x}`.trim())
                  .join(', ');
                // 站点名查不到时退回 id：名字缺失不该让整条冲突看不见
                const station = c.cooperationWarehouseName || `#${c.cooperationWarehouseId ?? '-'}`;
                return (
                  <div key={c.abMappingId ?? `${c.cooperationWarehouseId}-${addr}`} className="border-b border-gray-100 p-2 last:border-b-0">
                    <div className="text-sm font-medium text-gray-900">{station}</div>
                    <div className="text-xs text-gray-500">{addr || '-'}</div>
                    <div className="text-xs text-gray-400">
                      {[c.contactName, c.contactPhone].filter(Boolean).join(' · ') || '-'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  /**
   * 站点主体保存 + 后续各刀。
   *
   * ⚠️ **AB 冲突预检不在这里调**：本方法有两个调用点 —— handleOk 的正常入口，以及
   * 「地址相同/相似 → 仍要新建」确认后的重试（submitCount+1）。写在这里会让一次保存动作
   * 弹两次冲突框，第二次还套在前一个 Modal 的 onOk 里。弹多了运营就会无脑点确定，
   * 这道闸等于没有。故预检只在 handleOk 里调一次，重试路径天然复用那次确认结果。
   */
  const doSave = async (values: PickupSiteFormValues, submitCount: number): Promise<void> => {
    // 跳过标准化时两个 hash 必须齐备：后端 buildAnalyzedFromRequest 里 addressHash 直接取入参、**不兜底计算**，
    // 而 save() 是全列覆盖 UPDATE。传空会把站点 address_core_hash 写成 NULL、命中彻底失效
    //（「更新原站」场景更糟：把原站已有的 hash 直接抹掉）。条目 hash 理论上必有值（标准化失败也会走兜底算），
    // 但该字段前后端都可空，宁可拦住让运营反馈，也不能静默写坏命中键。
    if (skipStandardizeEffective && !presetAddressCoreHash) {
      message.error(wt('skipStandardizeHashMissing'));
      return;
    }
    let resp;
    try {
      resp = await saveWarehouse({
        variables: {
          input: {
            cooperationWarehouse: skipStandardizeEffective
              ? {
                  ...buildSaveData(values),
                  // 两个 hash 只在跳过标准化时传：常规建站由后端标准化后自算，传了也会被覆盖
                  addressCoreHash: presetAddressCoreHash,
                  addressAnalysisHash: presetAddressAnalysisHash,
                  ...unreachableSourcePayload(values),
                }
              : { ...buildSaveData(values), ...unreachableSourcePayload(values) },
            submitCount,
            // 恒传 boolean，不写 `|| undefined`：该字段在 schema 里是 Boolean（graphql-kotlin 不会把
            // Kotlin 默认值写进 SDL），漏传/传 null 都可能被拒。传 false 与不传等价，显式传最稳。
            skipAddressStandardize: skipStandardizeEffective,
          },
        },
      });
    } catch (e: any) {
      const keys: string[] = (e?.graphQLErrors ?? [])
        .map((ge: any) => ge?.extensions?.messageKey)
        .filter(Boolean);
      if (keys.includes('pickup.cooperation_warehouse.unreachable_not_allowed')) {
        // 闸门一（2.2 需求点1）：后端拒绝把站点置为「确认无法建联」。
        // 给明确指引而不是"保存失败请重试"——重试永远过不去，运营得先去上报地址异常。
        message.error(wt('unreachableNotAllowed'));
      } else if (keys.includes('errors.business.cooperationWarehouse.nameAlreadyExists')) {
        // 名称重复与「地址相同/相似」是两件事：后者可用「仍要新建」放行，前者只能改名，
        // 故这里给明确的改名指引，而不是笼统的"保存失败请重试"
        message.error(wt('nameDuplicated'));
      } else {
        message.error(e?.message || wt('saveFailed'));
      }
      return;
    }
    const payload: any = resp.data?.saveCooperationWarehouse;
    if (!payload) {
      message.error(wt('saveFailed'));
      return;
    }
    if (payload.result === 'success') {
      // saveCooperationWarehouse 成功分支不回传 originalRequest（后端契约缺陷），故回退到本抽屉已知的 cw id：
      // 审核池建站=草稿预占 id、编辑=既有站点 id，二者都等于最终落库 id。缺此回退则 onSaved 不触发→审核池条目不 resolve。
      const cwId = payload.originalRequest?.id ?? cooperationWarehouseId;
      // #6/G6：站点主体保存成功后，replace 整站 AB 映射集（cwId 为空则跳过，不阻断主流程）
      if (cwId != null) await persistAbMappings(Number(cwId), values.abMappings);
      // 二次建联：编辑「揽收成功/失败待建联(2/3)」站点且填了群 → 走状态机转已建联(link4)+op6。
      // saveCooperationWarehouse 只写群字段并保留原 link_status，link 流转交 submitSecondContact（避免绕过状态机/漏 op6）。
      // 判据用 dbLinkStatus（库里原值）而非 values.linkStatus：二次建联入口已把表单值预置成「已建联」，
      // 读表单值会让这里恒为 false —— 状态机不走、op6 不写，而 saveCooperationWarehouse 保留原 link_status，
      // 结果是界面显示已建联、库里还是待建联的静默不一致。
      // 2.1 需求点3 复用同一条路：编辑「确认无法建联(5)」站点并把状态改成「已建联(4)」时，
      // 同样交 submitSecondContact —— 它已具备「群ID/群名必填才放行」的后端校验 + transition(5→4)
      // 走状态机白名单 + 落 op6 日志。若走 saveCooperationWarehouse 裸写 link_status，
      // 三者全缺（白名单绕过、群校验只在前端、跟进记录缺这次变更，而需求点6 正要展示它）。
      // 判据同样用 dbLinkStatus（库里原值）：表单值此时已是 4，读表单值会恒为 false。
      const isUnreachableToLinked =
        dbLinkStatus === LinkStatus.Unreachable && values.linkStatus === LinkStatus.Linked;
      const doSecondContact =
        mode === 'edit' &&
        cwId != null &&
        (dbLinkStatus === LinkStatus.PickedUpPending ||
          dbLinkStatus === LinkStatus.PickupFailedPending ||
          isUnreachableToLinked) &&
        !!values.groupId?.trim() &&
        !!values.groupName?.trim();
      if (doSecondContact) {
        try {
          const r = await submitSecondContactMutation({
            variables: {
              input: {
                cooperationWarehouseId: Number(cwId),
                contacted: true,
                groupId: values.groupId!.trim(),
                groupName: values.groupName!.trim(),
              },
            },
          });
          if (!r.data?.submitSecondContact) message.warning(wt('secondContactFailed'));
        } catch (e: any) {
          message.warning(e?.message || wt('secondContactFailed'));
        }
      }
      message.success(mode === 'edit' ? wt('updated') : wt('created'));
      if (cwId != null) onSaved?.(Number(cwId));
    } else if (payload.result === 'same' || payload.result === 'similar') {
      // 命中相同/相似地址：确认后按 submitCount+1 强制建站（复用既有查重交互口径）
      Modal.confirm({
        title: wt('dupTitle'),
        content: payload.message || wt('dupContent'),
        okText: wt('forceCreate'),
        cancelText: t('common.cancel', '取消') as string,
        onOk: () => doSave(values, (payload.submitCount ?? submitCount) + 1),
      });
    } else {
      message.error(payload.message || wt('saveFailed'));
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      // AB 行改为「单行完整地址 + 标准化回填四段」后，四段只由标准化写入：
      // 若运营填了地址却没点标准化，persistAbMappings 会因 addressLine1 为空把整行静默丢弃 → 此处显式拦截。
      // 注意：这道拦截**刻意不受 skipStandardize 影响**。skipStandardize 说的是"本站主地址标准化不了"，
      // 而 AB 行是运营主动录入的错误地址映射，未标准化就落库会写出错的 core_hash、污染后续命中。
      // AB 行本身是选填的——不填就不会被拦，不构成"建不出站点"。
      if (abRowsNeedStandardize(values.abMappings)) {
        message.warning(wt('abStandardizeHint'));
        return;
      }
      // 命中键不全的 AB 行：写进去永远命不中，且界面看不出来。拦在提交前，不让它变成静默死数据。
      if (abRowsMissingHitKey(values.abMappings)) {
        message.warning(wt('abHitKeyRequired'));
        return;
      }
      if (warehouseId != null && addressState) {
        // 真实保存：揽收地址来自 addressState（审核池预标准化 / 表单标准化按钮结果）；PRD 要求提交时已标准化
        // 2.1 需求点1：skipStandardize 场景该地址本就标准化不了，这道校验必须放行，否则永远提交不了
        if (!skipStandardizeEffective && !values.addrFields?.standardized) {
          message.warning(wt('standardize'));
          return;
        }
        // D2：先问清楚「这次保存会失效掉谁的映射」，运营确认后才动站点主体。
        // 放在这里而不是 doSave 里：doSave 还会被「仍要新建」的重试再调一次，写在那边会弹两次。
        if (!(await confirmAbConflicts(values))) return;
        // 拆分场景首次即 submitCount=2 跳过查重（拆出地址必然命中源站关联地址，是误报）
        await doSave(values, skipDedupOnSave ? 2 : 1);
        return;
      }
      // 值回传路径（未提供 warehouseId）：由调用方处理（如司机反馈建联 → submitSecondContact）。结果 toast 交调用方。
      if (!skipStandardizeEffective && !values.addrFields?.standardized) {
        message.warning(wt('standardize'));
        return;
      }
      onSubmit?.(values);
    } catch (e: any) {
      // 业务异常已在 doSave 内 toast；此处原先是空 catch，把业务异常也一起吞了造成静默失败。
      //
      // 判「是不是校验异常」必须看 errorFields **存不存在**，不能看 errorFields[0]：
      // rc-field-form 在「本次校验被后一次 validateFields 顶掉」（outOfDate）且本次全通过时，
      // 会 reject 出 `{ errorFields: [] }` —— 空数组。用 [0] 判就会把它当成业务异常、
      // 误弹「站点保存失败」。本组件的 handleValuesChange 在工作日变化时就会再调一次
      // validateFields，这条路径真实存在（窗口极窄，但那正是它测不出来的原因）。
      if (e?.errorFields) {
        const first = e.errorFields[0];
        if (!first) return; // outOfDate 且无错误：与改动前一致，静默返回
        // 校验失败不能只靠行内红字：排期块在表单最下方（名称/地址/联系人/别名/关联地址之后），
        // 而 2.2 需求点3 之后新建站点的四个时间框默认为空，首个错误几乎总落在那里 ——
        // 运营填完首屏必填项点「确定」，红字在折叠线以下，表现就是**按钮毫无反应**。
        // 文案直接取该字段自己的 rules message（已是 i18n 文案，如「请选择营业时间」），
        // 比笼统的「请完成必填项」更能让人一眼知道缺什么，也不用再开一个 i18n key。
        // 用 form.scrollToField 而非 Form 的 scrollToFirstError：后者只在 form.submit()
        // 流程里生效，本方法是手动调 validateFields，挂上去不会触发。
        if (first.errors?.[0]) message.warning(first.errors[0]);
        form.scrollToField(first.name, { block: 'center' });
        return;
      }
      console.error('site save failed', e);
      message.error(wt('saveFailed'));
    }
  };

  useImperativeHandle(ref, () => ({ submit: handleOk }));

  return (
      <Form
        className="pickup-site-form"
        form={form}
        layout="horizontal"
        labelAlign="right"
        labelCol={{ flex: '148px' }}
        wrapperCol={{ flex: 'auto' }}
        disabled={readOnly}
        colon={false}
        onValuesChange={handleValuesChange}
      >
        {/* 对齐 PRD：label 不带冒号、右侧留间隙（避免与输入框贴合）；必填标识按 rules 常显（含查看模式） */}
        <style>{`.pickup-site-form .ant-form-item-label > label { padding-inline-end: 12px; }`}</style>
        {/* ID + 模式徽标（独立一行，对齐 PRD） */}
        <div className="mb-4 flex items-center gap-3">
          <span className="w-[136px] text-right text-gray-500">ID</span>
          <span className="font-semibold text-gray-800">{cooperationWarehouseId ?? '—'}</span>
          {mode === 'create' && <Tag color="purple">{wt('newBadge')}</Tag>}
          {mode === 'view' && <Tag>{wt('viewBadge')}</Tag>}
        </div>

        {/* 基础信息（2列） */}
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item name="name" label={wt('name')} rules={[{ required: true }, maxLenRule(100)]}>
              <Input placeholder={wt('inputPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="linkStatus" label={wt('status')} rules={[{ required: true }]}>
              <Select options={statusOptions} disabled={!statusEditable || readOnly} allowClear placeholder={wt('selectPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* 2.1 需求点3：海外仓类型仅「已建联(4)」必填；待上门(1)/上门中(6)/揽收成功待建联(2)/
                揽收失败待建联(3)/确认无法建联(5) 一律非必填——这些状态下站点尚未真正建立合作关系，
                仓库类型往往还问不到。同时覆盖需求点1「创建新站点（确认无法建联）」的免校验要求。 */}
            <Form.Item
              name="warehouseType"
              label={wt('warehouseType')}
              rules={linkStatus === LinkStatus.Linked ? [{ required: true }] : []}
            >
              <Select options={warehouseTypeOptions} allowClear placeholder={wt('selectPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="brand" label={wt('brand')} rules={[maxLenRule(100)]}>
              <Input placeholder={wt('inputPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="subBrand" label={wt('subBrand')} rules={[maxLenRule(100)]}>
              <Input placeholder={wt('inputPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="otherRemark" label={wt('otherRemark')} rules={[maxLenRule(200)]}>
              <Input placeholder={wt('inputPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        {/* 地址 & 联系人信息 */}
        <div className="mb-3 mt-4 flex items-center gap-2 text-[15px] font-semibold text-gray-800">
          🏠 {wt('sectionAddress')}
        </div>
        {/* 揽收地址：分段输入（街道+Dock 一行 / 州+城市+邮编 一行）。
            单文本框已废弃——无论前端按逗号拆还是后端 parseRawAddress 拆都在猜分段，街道自带逗号即拆错。
            Dock 已并入本组件，不再有独立 Form.Item；标准化状态由 addrFields.standardized 单一持有。 */}
        <Form.Item label={wt('pickupAddress')} required name="addrFields">
          <PickupAddressFields
            readOnly={readOnly}
            onStandardized={handleAddressStandardized}
            standardizeSkipped={skipStandardizeEffective}
          />
        </Form.Item>

        <Form.Item label={wt('contactLabel')} required>
          <Form.List name="contactPersons">
            {(fields, { add, remove }) => {
              // PRD 2.2 需求点4：默认仅展示 3 条，超过时走「查看更多联系人」弹窗。
              // 弹窗内**可编辑**（产品 2026-10-13 定：都可编辑），与 PRD 配图的只读样式不同。
              const visibleFields = showAllContacts ? fields : fields.slice(0, CONTACT_VISIBLE_LIMIT);
              const hiddenFields = showAllContacts ? [] : fields.slice(CONTACT_VISIBLE_LIMIT);
              /** 一行联系人（姓名/国家码/电话/分机 + 删除）。主区与弹窗共用，避免两处校验规则漂移。 */
              const renderContactRow = (
                { key, name, ...rest }: (typeof fields)[number],
                opts: { showAdd: boolean },
              ) => (
                <Space key={key} align="baseline">
                  {/* 联系人必填。不用 noStyle：noStyle 不渲染校验红字，运营点「确定」会没有任何反应 */}
                  <Form.Item
                    {...rest}
                    name={[name, 'name']}
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: wt('contactPersonRequired') }]}
                  >
                    <Input placeholder={wt('contactPerson')} style={{ width: 130 }} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'phoneCountryCode']} noStyle initialValue="+1">
                    <Select style={{ width: 74 }} options={[{ label: '+1', value: '+1' }]} />
                  </Form.Item>
                  {/* validateTrigger 多挂一个 onBlur，是为了让去重规则能在「离开输入框」时触发。
                      对 required 的影响：**判定结果不变**（空仍然报必填、非空仍然通过），
                      但**报错时机提前了** —— 点进空的新行再点走，现在当场出红字，
                      以前要到点「确定」才出。属于可接受的既有行为变化，与同文件
                      「不用 noStyle，否则点确定没有任何反应」的取向一致。 */}
                  <Form.Item
                    {...rest}
                    name={[name, 'phone']}
                    style={{ marginBottom: 0 }}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={[{ required: true, message: wt('phoneRequired') }, phoneUniqueRule(name)]}
                  >
                    <Input placeholder={wt('phone')} style={{ width: 150 }} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'extension']} noStyle>
                    <Input placeholder={wt('extension')} style={{ width: 80 }} />
                  </Form.Item>
                  {!readOnly && fields.length > 1 && (
                    <MinusOutlined className="cursor-pointer text-gray-400" onClick={() => remove(name)} />
                  )}
                  {/* 「+」只挂在主区最后一个**可见**行上：折叠时 fields.length-1 指向被折叠的行，
                      按原写法整个加号就消失了。新增后展开全部，否则新行排在第 4 位会当场看不见。 */}
                  {!readOnly && opts.showAdd && (
                    <PlusCircleOutlined
                      className="cursor-pointer text-purple-600"
                      onClick={() => {
                        add();
                        setContactsModalOpen(false);
                        setShowAllContacts(true);
                      }}
                    />
                  )}
                </Space>
              );
              return (
                <div className="flex flex-col gap-2">
                  {visibleFields.map((f, idx) =>
                    renderContactRow(f, { showAdd: idx === visibleFields.length - 1 }),
                  )}
                  {hiddenFields.length > 0 && (
                    <Button
                      type="link"
                      size="small"
                      className="self-start px-0"
                      onClick={() => setContactsModalOpen(true)}
                    >
                      {wt('viewMoreContacts')}（{hiddenFields.length}）
                    </Button>
                  )}
                  {/* 「查看更多联系人」弹窗：只渲染第 4 条及以后，可编辑。
                      不把前 3 条也放进来 —— 同一 namePath 在主区与弹窗重复注册会让 antd 的
                      字段实例互相覆盖，scrollToField 也会定位到错的那个。
                      ⚠️ 弹窗关闭时这些行**不渲染 ⇒ 不参与校验**；存量脏数据的兜底走
                      showAllContacts（打开表单时检测到隐藏行不完整就直接内联展开全部）。 */}
                  <Modal
                    open={contactsModalOpen}
                    title={wt('contactInfoTitle')}
                    width={620}
                    onCancel={closeContactsModal}
                    onOk={closeContactsModal}
                    okText={t('common.confirm', '确定') as string}
                    cancelButtonProps={{ style: { display: 'none' } }}
                  >
                    <div className="flex max-h-96 flex-col gap-2 overflow-auto pt-2">
                      {hiddenFields.map((f) => renderContactRow(f, { showAdd: false }))}
                    </div>
                  </Modal>
                </div>
              );
            }}
          </Form.List>
        </Form.Item>

        {showGroupFields && (
          <>
            {isSecondContactEditable && (
              <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 12 }}>{wt('secondContactHint')}</div>
            )}
            <Row gutter={24}>
              <Col span={12}>
                {/* 群ID/群名称必填门控 = showGroupFields：已建联(requireGroup)必填；二次建联(isSecondContactEditable)
                    也必填——填全群才走 submitSecondContact 转已建联，否则只填一个会静默跳过状态机、状态停在待建联。 */}
                <Form.Item name="groupId" label={wt('groupId')} rules={showGroupFields ? [{ required: true }] : []}>
                  <Input placeholder={wt('inputPlaceholder')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="groupName" label={wt('groupName')} rules={showGroupFields ? [{ required: true }] : []}>
                  <Input placeholder={wt('inputPlaceholder')} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* 别名（alias_json）：订单里出现过的同一站点的其它叫法，供人工识别。
            老抽屉（非灰度 hub 仍在用）一直有这个字段；本表单取代它成为灰度 hub 的编辑入口后必须补上，
            否则运营在这里保存一次就把别名清空了（后端对该列是无条件写）。
            用 tags 模式而非动态 Input 列表：值就是一个字符串数组，tags 自带增删、更紧凑。 */}
        <Form.Item name="aliasList" label={wt('alias')}>
          <Select
            mode="tags"
            tokenSeparators={[',']}
            placeholder={wt('aliasPlaceholder')}
            disabled={readOnly}
          />
        </Form.Item>

        {/* ===== 透传字段：本表单不展示、不编辑，只把站点原值原样带回保存 =====

            `saveCooperationWarehouse` 是 **PUT 语义 + 全列覆盖**，这些列不发就被写 NULL。
            下游各有人在读：
              latitude/longitude  预约单详情 cooperationWarehouseLatitude、POP 详情 siteLatitude、路线规划
              email               预约单 contactEmail
              groupQrcodeS3Key    建联群二维码
              useVehicleModelId   已废弃列，但预约单链路仍读它派车
              channelId           长期揽收调度器按 `channel_id is null` 硬过滤

            ★ **必须注册成隐藏 Form.Item，只放进 initialValues 是没用的**：
            `form.validateFields()` 解析出的是「已注册 namePath 列表」对应的值，
            store 里没有对应 Form.Item 的 key 会被整个丢掉。rc-field-form 2.7.1 实测 ——
            store 有 `{email, latitude, name}` 时 `getFieldsValue()` 返回 `{}`。
            所以**往 buildSaveData 里新加任何透传字段，都要在这里同步加一行**，否则它恒为 null。 */}
        <Form.Item name="latitude" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="longitude" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="email" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="groupQrcodeS3Key" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="useVehicleModelId" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="channelId" hidden>
          <Input />
        </Form.Item>

        {/* 关联地址（related_address_json 只读列表）+ 拆分站点（edit 模式启用，onSplit 由 useStationViewer 编排：选 type2 关联地址→建新站点→resolveStationSplit 迁移 relate，#5） */}
        <Form.Item label={wt('relatedAddress')}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {relateAddresses && relateAddresses.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {relateAddresses.map((r) => {
                    // 完整地址口径与拆分站点弹窗一致：街道 · 单元 · 城市 · 州 · 邮编，缺项自动跳过
                    const addr = [r.addressLine1, r.addressLine2, r.city, r.state, r.zipcode]
                      .filter((x) => x && `${x}`.trim())
                      .join(', ');
                    return (
                      <div key={r.relateId} className="text-sm text-gray-700">
                        {addr || '-'}
                      </div>
                    );
                  })}
                </div>
              ) : (
                // 无关联地址就是无，不能显示「后续接入」——那是功能未上线时的占位文案，
                // 现在关联地址已接入，留着会让运营误以为功能还没做
                <div className="text-sm text-gray-400">-</div>
              )}
            </div>
            {/* 无关联地址不展示「拆分站点」：拆分即把关联地址迁到新站，没关联地址无从拆 */}
            {!readOnly && onSplit && relateAddresses && relateAddresses.length > 0 && (
              <Button type="primary" onClick={onSplit}>
                {wt('splitSite')}
              </Button>
            )}
          </div>
        </Form.Item>

        {/* 揽收信息 */}
        <div className="mb-3 mt-4 flex items-center gap-2 text-[15px] font-semibold text-gray-800">
          📋 {wt('sectionPickup')}
        </div>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item name="standingPickup" label={wt('standingPickup')} rules={[{ required: true }]}>
              <Select
                options={[
                  { label: wt('yes'), value: true },
                  { label: wt('no'), value: false },
                ]}
                // 排期块已长期/非长期共用，切换时不再清空排期（此前切到「否」会把整块排期重置成空行）；
                // 仅「长期揽收预约客户」是长期专有，切到「否」时清掉，避免隐藏字段被一并提交
                onChange={(v: boolean) => {
                  if (!v) form.setFieldsValue({ standingClientId: undefined });
                }}
              />
            </Form.Item>
          </Col>
          {standingPickup && (
            <Col span={12}>
              <Form.Item
                name="standingClientId"
                label={wt('standingClient')}
                rules={[{ required: true, message: wt('standingClientRequired') }]}
              >
                <Select options={clientOptions} allowClear showSearch optionFilterProp="label" placeholder={wt('selectPlaceholder')} />
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item
              name="relatedClientIds"
              label={wt('relatedClient')}
              rules={[{ required: true, message: wt('relatedClientRequired') }]}
            >
              <Select mode="multiple" options={clientOptions} allowClear showSearch optionFilterProp="label" placeholder={wt('selectPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        {/* 排期块非 Form.Item，默认会横跨全宽、与上方带 label 的字段错位；左缩进对齐 labelCol(148px) 的 wrapper 起点。
            第 2 列 288px：两个 TimePicker(各 120) + Space 间距 + 分隔符，260px 装不下会撑出横向滚动条。
            长期/非长期共用同一排期块（原非长期的工作日+期望时间+车型三个独立字段已并入），故不再按 standingPickup 门控。 */}
        {(
          <div className="mb-3 ml-[148px] rounded-lg border border-gray-200 bg-gray-50 p-3">
            {/* 2.2 需求点3：营业时间与期望上门时间**各占一列**，与 PRD 原型一致（产品 2026-10-13 定）。
                宽度是算过的：五列固定部分 = 240+240+200+32 + 四个 gap 48 = 760px。
                可用宽度 = 壳宽 - 壳内边距48 - 本块左缩进148 - 块内 padding24，
                所以两个壳都必须 ≥1280（抽屉 1280 / AB 向导 Modal 1280），留给「工作日」300px。
                ⚠️ 谁要调窄这两个壳，先回来重算这一行，否则「工作日」多选会被挤塌。 */}
            <div className="mb-2 grid grid-cols-[minmax(0,1fr)_240px_240px_200px_32px] gap-3 text-[13px] text-gray-500">
              <span>{wt('workWeekdays')}</span>
              <span>{wt('businessTime')}</span>
              <span>{wt('expectedTime')}</span>
              <span>{wt('vehicleModel')}</span>
              <span />
            </div>
            <Form.List name="standingSchedules">
              {(fields, { add, remove }) => (
                <div className="flex flex-col gap-2">
                  {fields.map(({ key, name, ...rest }, idx) => (
                    <div key={key} className="grid grid-cols-[minmax(0,1fr)_240px_240px_200px_32px] items-start gap-3">
                      <Form.Item
                        {...rest}
                        name={[name, 'weekdays']}
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: wt('workWeekdaysRequired') }, weekdayUniqueRule(name)]}
                      >
                        <Select
                          mode="multiple"
                          options={weekdayOptions}
                          placeholder={wt('workWeekdays')}
                          disabled={readOnly}
                        />
                      </Form.Item>
                      {/* 营业时间（仓库几点开门）—— 独立一列，与 PRD 原型一致。
                          它**只落 standing_schedule_json**、不回写站点级列；建单链路一律取
                          期望上门时间 ⇒ 对既有建单零回归。 */}
                      {/* align="baseline" 不能省：Space 横向时 antd 默认 align=center，
                          两个时间框只填一个时，另一个下方撑出红字变高，居中对齐会把没报错的那个
                          **往下推**（视觉上「框掉下来」）。按基线对齐则红字只向下延伸、不挪动输入框。
                          口径与本文件联系人行的 <Space align="baseline"> 一致。 */}
                      <Space size={4} align="baseline">
                        <Form.Item
                          {...rest}
                          name={[name, 'businessStartTime']}
                          style={{ marginBottom: 0 }}
                          rules={[{ required: true, message: wt('businessTimeRequired') }]}
                        >
                          <TimePicker format="HH:mm" placeholder="09:00" style={{ width: 104 }} disabled={readOnly} />
                        </Form.Item>
                        <span className="text-gray-400">—</span>
                        <Form.Item
                          {...rest}
                          name={[name, 'businessEndTime']}
                          style={{ marginBottom: 0 }}
                          dependencies={[['standingSchedules', name, 'businessStartTime']]}
                          rules={[
                            { required: true, message: wt('businessTimeRequired') },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const start = getFieldValue(['standingSchedules', name, 'businessStartTime']);
                                if (!value || !start) return Promise.resolve();
                                return value.isAfter(start)
                                  ? Promise.resolve()
                                  : Promise.reject(new Error(wt('endTimeAfterStart') as string));
                              },
                            }),
                          ]}
                        >
                          <TimePicker format="HH:mm" placeholder="18:00" style={{ width: 104 }} disabled={readOnly} />
                        </Form.Item>
                      </Space>
                      {/* 期望上门时间（司机几点到）—— 独立一列。本列是 expected_pickup_start/end_time
                          与 work_start/end_time 的回写来源，两列同源写排期首行。 */}
                      {/* align="baseline" 的理由同上一列（营业时间）。 */}
                      <Space size={4} align="baseline">
                        <Form.Item
                          {...rest}
                          name={[name, 'startTime']}
                          style={{ marginBottom: 0 }}
                          rules={[{ required: true, message: wt('expectedTimeRequired') }]}
                        >
                          <TimePicker format="HH:mm" placeholder="09:00" style={{ width: 104 }} disabled={readOnly} />
                        </Form.Item>
                        <span className="text-gray-400">—</span>
                        {/* 结束时间必须晚于开始时间：本行是 expected_pickup_start/end_time 的回写来源，
                            相等会让预约单创建在 validatePickupAppointment 处抛 ValidationException（start >= end）。
                            不能用 noStyle——那样校验红字不会渲染，故改为零 margin 的普通 Form.Item。 */}
                        <Form.Item
                          {...rest}
                          name={[name, 'endTime']}
                          style={{ marginBottom: 0 }}
                          dependencies={[['standingSchedules', name, 'startTime']]}
                          rules={[
                            { required: true, message: wt('expectedTimeRequired') },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const start = getFieldValue(['standingSchedules', name, 'startTime']);
                                if (!value || !start) return Promise.resolve();
                                return value.isAfter(start)
                                  ? Promise.resolve()
                                  : Promise.reject(new Error(wt('endTimeAfterStart') as string));
                              },
                            }),
                          ]}
                        >
                          <TimePicker format="HH:mm" placeholder="18:00" style={{ width: 104 }} disabled={readOnly} />
                        </Form.Item>
                      </Space>
                      <Form.Item
                        {...rest}
                        name={[name, 'vehicleTypeId']}
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: wt('vehicleRequired') }]}
                      >
                        <Select
                          options={vehicleTypeOptions}
                          placeholder={wt('selectPlaceholder')}
                          showSearch
                          optionFilterProp="label"
                          disabled={readOnly}
                        />
                      </Form.Item>
                      {/* self-center：行容器是 items-start，工作日换行时整行会变高，按钮只能贴在行顶。
                          只给按钮这一格改对齐，不动其它列——时间选择器与表头仍按顶对齐。 */}
                      <div className="flex self-center items-center justify-center text-purple-600">
                        {/* 每行**只有一个**按钮：首行固定是「+」，其余行固定是「−」。
                            首行恒为「+」⇒ 加行入口位置固定，不随行数跳来跳去；
                            其余行恒为「−」⇒ 行数与可删数一一对应，最后一行（即首行）删不掉，
                            「排期至少一行」自然成立，不需要再判 fields.length。
                            ⚠️ 代价：**第一组排期删不掉**。要换掉它只能就地改值；
                            工作日有跨行互斥校验，改的过程中会短暂标红，属预期。 */}
                        {!readOnly &&
                          (idx === 0 ? (
                            <PlusCircleOutlined
                              className="cursor-pointer"
                              onClick={() => add(nextScheduleRow())}
                            />
                          ) : (
                            <MinusOutlined className="cursor-pointer text-gray-400" onClick={() => remove(name)} />
                          ))}
                      </div>
                    </div>
                  ))}
                  {/* 0 行的入口：`cwFormMapper` 的三级回落全落空时 standingSchedules 为 undefined ⇒ 渲染 0 行，
                      而「+」挂在行上 ⇒ 没有行就没有「+」，运营再也加不回来。
                      ⚠️ 这里刻意**不自动塞一行**：排期行的工作日 / 营业时间 / 期望上门时间 / 车型**全是必填**，
                      自动塞行会让这类站点连改个名字都保存不了（必填项全空）。要让存量站点都有排期，
                      是数据侧的事，走 fixInitStandingScheduleJson 订正，不在表单上硬塞。口径同本文件黑名单块。 */}
                  {fields.length === 0 && !readOnly && (
                    <a className="text-purple-600" onClick={() => add(newScheduleRow())}>
                      <PlusCircleOutlined /> {wt('addSchedule')}
                    </a>
                  )}
                </div>
              )}
            </Form.List>
          </div>
        )}

        <Form.Item name="remark" label={wt('remark')} rules={[maxLenRule(200)]}>
          <Input.TextArea rows={2} placeholder={wt('inputPlaceholder')} />
        </Form.Item>

        {/* 黑名单配置（独立区；落库方案A/B 待确认 → todo wuhao） */}
        <div className="mb-1 mt-4 flex items-center gap-2 text-[15px] font-semibold text-gray-800">
          ⚠️ {wt('sectionBlacklist')}
        </div>
        <div className="mb-2 text-[13px] text-gray-500">{wt('blacklistDesc')}</div>
        <Form.List name="blacklistContacts">
          {(fields, { add, remove }) => (
            <div className="flex flex-col gap-2">
              {fields.map(({ key, name, ...rest }, idx) => (
                <Space key={key} align="baseline">
                  <Form.Item {...rest} name={[name, 'phoneCountryCode']} noStyle initialValue="+1">
                    <Select style={{ width: 84 }} options={[{ label: '+1', value: '+1' }]} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'phone']} noStyle>
                    <Input placeholder={wt('phone')} style={{ width: 200 }} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'extension']} noStyle>
                    <Input placeholder={wt('extension')} style={{ width: 110 }} />
                  </Form.Item>
                  {!readOnly && fields.length > 1 && (
                    <MinusOutlined className="cursor-pointer text-gray-400" onClick={() => remove(name)} />
                  )}
                  {!readOnly && idx === fields.length - 1 && (
                    <PlusCircleOutlined className="cursor-pointer text-purple-600" onClick={() => add()} />
                  )}
                </Space>
              ))}
              {fields.length === 0 && !readOnly && (
                <a className="text-purple-600" onClick={() => add()}>
                  <PlusCircleOutlined /> {wt('addBlacklist')}
                </a>
              )}
            </div>
          )}
        </Form.List>

        {/* AB 地址映射（#6/G6：错误下单地址 A → 本站点；结构化四段 A 地址由后端标准化算 coreHash，replace 语义整站重写） */}
        <div className="mb-2 mt-4 flex items-center gap-2 text-[15px] font-semibold text-gray-800">
          🔗 {wt('sectionAbMapping')}
        </div>
        <Form.List name="abMappings">
          {(fields, { add, remove }) => (
            <div className="flex flex-col gap-2">
              {fields.map(({ key, name, ...rest }) => (
                <div key={key} className="rounded-lg border border-gray-200 p-2">
                  {/* 隐藏：AB 行标准化标记（加载/标准化按钮维护，保存不依赖，仅前端提示） */}
                  <Form.Item {...rest} name={[name, 'standardized']} hidden>
                    <Input />
                  </Form.Item>
                  {/* A 地址：与主揽收地址同一分段组件（compact 布局）。四段由标准化写入隐藏字段供保存，
                      不直接编辑——避免运营只填街道就提交、以及四段与实际输入不一致。 */}
                  <Form.Item {...rest} name={[name, 'addrFields']} noStyle>
                    <PickupAddressFields
                      readOnly={readOnly}
                      compact
                      onStandardized={(resolved) => handleAbStandardized(name, resolved)}
                    />
                  </Form.Item>
                  {/* 标准化结果只读回显（四段值随表单保存，隐藏字段承载） */}
                  <Form.Item {...rest} name={[name, 'addressLine1']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'city']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'state']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'zipcode']} hidden>
                    <Input />
                  </Form.Item>
                  <Row gutter={8} className="mt-2" align="middle">
                    <Col span={7}>
                      <Form.Item {...rest} name={[name, 'contactName']} noStyle>
                        <Input placeholder={wt('contactPerson')} />
                      </Form.Item>
                    </Col>
                    {/* 联系方式与客户渠道是 AB 命中键的两个维度，缺任一该行永远命不中 ⇒ 标必填。
                        联系人不标：它参与命中但允许为空（NULL-safe 比较，只命中同为空的映射）。 */}
                    <Col span={7}>
                      <Form.Item {...rest} name={[name, 'contactPhone']} noStyle>
                        <Input placeholder={`${wt('contactMethod')} *`} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item {...rest} name={[name, 'channelId']} noStyle>
                        <Select
                          options={clientOptions}
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder={`${wt('clientChannel')} *`}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={2} className="flex justify-center">
                      {!readOnly && (
                        <MinusOutlined className="cursor-pointer text-gray-400" onClick={() => remove(name)} />
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
              {/* 原先无条件渲染：一个 AB 行都没填也照样警告，界面看起来像被拦住。
                  改为只在「确实有填了地址、但没点标准化的行」时提示，与提交拦截判据保持一致。 */}
              {!readOnly && abNeedsStandardize && (
                <div className="mt-1 text-[13px] text-orange-500">⚠ {wt('abStandardizeHint')}</div>
              )}
              {!readOnly && (
                <div className="mt-1 text-center">
                  <a className="text-purple-600" onClick={() => add()}>
                    <PlusCircleOutlined /> {wt('addErrorAddress')}
                  </a>
                </div>
              )}
            </div>
          )}
        </Form.List>

        {/*
          站点跟进记录（2.1 需求点7）。**仅「确认无法建联」站点展示**（产品定案 2026-08-26）：
          其余状态的跟进记录由「司机反馈跟进」tab 承载，这里重复展示只会挤占档案字段。
          放在最底部（AB 地址映射之后）—— 补充信息，不该插在主档案字段中间。
          用 dbLinkStatus 而不是 Form.useWatch 的 linkStatus：后者是表单当前值，编辑态下运营
          刚把状态改成别的、还没保存时，跟进记录会跟着闪没；这里要的是「库里这个站点是不是 link5」。
          空态由子组件自己渲染，不在这里判有无记录：50.7% 的 link5 站点没有记录，
          整块隐藏会让运营分不清"没这功能"还是"没记录"。
        */}
        {v2Enabled && dbLinkStatus === LinkStatus.Unreachable && cooperationWarehouseId != null && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <StationFollowUpTimeline cooperationWarehouseId={cooperationWarehouseId} open={open} />
          </div>
        )}
      </Form>
  );
});
PickupSiteFormBody.displayName = 'PickupSiteFormBody';

/**
 * 站点表单抽屉（Modal 壳）：创建/编辑/查看入口用。内容复用 PickupSiteFormBody；
 * footer「确认」触发 body.submit()，成功保存/值回传后由本壳统一 onClose（保持对外契约不变）。
 */
const PickupSiteFormDrawer: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const bodyRef = useRef<PickupSiteFormBodyRef>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const readOnly = props.mode === 'view';

  // ===== 2.2 需求点2：把**当前站点**整体映射到目标站点 B（整站搬走）=====
  // ⚠️ 与表单体里那个内联「AB 地址映射」区块**方向相反**：那个是「错误地址 → 当前站点」(本站当 B)，
  //    这个是「当前站点 → 目标站点」(本站当 A)。两者可以同时存在，别改错地方。
  const [abOpen, setAbOpen] = useState(false);
  const [abChecking, setAbChecking] = useState(false);
  const [previewStationRetire] = useLazyQuery<
    PreviewStationAbRetireData,
    { cooperationWarehouseId: number; targetBCooperationWarehouseId: number | null; fullySelected: boolean }
  >(PREVIEW_STATION_AB_RETIRE, { fetchPolicy: 'network-only' });
  const [resolveStationAb] = useMutation<
    ResolveStationAsAbMappingResponse,
    { input: ResolveStationAbMappingInput }
  >(RESOLVE_STATION_AS_AB_MAPPING, { fetchPolicy: 'no-cache' });

  const abSourceStationId = props.cooperationWarehouseId;
  const abHubId = props.stationHubId ?? props.warehouseId;
  /**
   * 「AB地址映射」按钮只在**编辑既有站点**时出现。
   * 新建态没有站点 id（映射不了一个还不存在的 A）；查看态是只读，不提供写操作。
   */
  const showAbButton =
    props.showAbEntry === true && props.mode === 'edit' && abSourceStationId != null && abHubId != null;

  /**
   * 向导里「当前下单地址」的来源，口径与表单体那个 `seeded` 完全一致：
   * 审核池流地址在 `addressForSave`，编辑流才在 `initialValues` 四段。
   *
   * ⚠️ 只读 `initialValues` 会**恒为空**：站点管理走 useStationViewer，`mapCwToForm`
   * 把四段放进 `address`(→addressForSave)，`values`(→initialValues) 里根本没有这几个键 ——
   * 而站点管理是 AB 按钮唯一的入口。可选链读不存在的键只会得到 undefined，编译与单测都看不见。
   *
   * 刻意**不读** `addressState`：那是表单里可被运营改写的当前值，而后端做 AB 映射读的是
   * **库里**的站点地址（core_hash 由 DB 行算）。显示未保存的编辑会让运营以为搬的是改后的地址。
   */
  const abAddrSource = props.addressForSave ?? props.initialValues;
  const abDisplayAddress = [
    abAddrSource?.addressLine1,
    abAddrSource?.city,
    abAddrSource?.state,
    abAddrSource?.zipcode,
  ]
    .filter((x) => !!x && String(x).trim() !== '')
    .join(', ');

  /**
   * 入口 guard：照抄审核池那条路的 `ReviewAddressDetail#guardAbMappingEntry` ——
   * 点按钮时先问一次「假设全选会怎样」(targetB=null, fullySelected=true)，把拦截理由提前摊开，
   * 否则运营填完整个向导才被告知站点不会退役。
   */
  const openAbWizard = async () => {
    if (abSourceStationId == null) return;
    setAbChecking(true);
    try {
      const { data } = await previewStationRetire({
        variables: {
          cooperationWarehouseId: abSourceStationId,
          targetBCooperationWarehouseId: null,
          fullySelected: true,
        },
      });
      const p = data?.previewStationAbRetire;
      if (!p) {
        // 预检没取到结论：**不静默放行**。向导仍可进（权威拦截在提交时），但必须让运营知道
        // 他看到的「会不会退役」是未知的 —— 静默放行会让他以为预检通过了。
        message.warning(wt('abRetirePreviewUnavailable'));
      } else if (p.existingAbSourceMappingCount > 0) {
        // 并发互覆盖提示（2.2 需求点2）：站点入口没有审核池条目那样的状态位判重，
        // 两个运营先后打开同一站点各自映射到不同的 B，后提交的会让先提交的失效而双方都看到成功。
        // ⚠️ 提示不是锁 —— 从这里到提交之间仍有窗口。
        message.warning(
          wt('abAlreadyMappedWarning')
            .replace('{count}', String(p.existingAbSourceMappingCount))
            .replace('{targets}', p.existingAbTargetCooperationWarehouseIds.join(', ')),
        );
      } else if (p.applicable && !p.willRetire && p.blockedReason) {
        // 会被闸门挡住（长期揽收 / 已是他人 AB 目标 / 量级超限 / 有进行中预约单…）：
        // 只写映射、不退役。这是**既有行为**，不拦，但要在进向导前说清楚。
        message.info(wt('abWillNotRetire'));
      }
    } catch {
      message.warning(wt('abRetirePreviewUnavailable'));
    } finally {
      setAbChecking(false);
    }
    setAbOpen(true);
  };

  const handleAbSubmit = async (payload: AbMappingSubmit) => {
    if (abSourceStationId == null || abHubId == null) return;
    try {
      const { data } = await resolveStationAb({
        variables: {
          input: {
            sourceCooperationWarehouseId: abSourceStationId,
            targetBCooperationWarehouseId: payload.targetSiteId,
            // 必传：api-server 的仓库维度鉴权按字段名反射取它，取不到整个请求会被拒
            warehouseId: abHubId,
            mappings: payload.mappings,
            fullySelected: payload.fullySelected,
          },
        },
      });
      if (data?.resolveStationAsAbMapping) {
        message.success(t('common.operateSuccess', '操作成功') as string);
        setAbOpen(false);
        // AB 映射可能已把本站**整站退役**，表单数据随即失效 ⇒ 关表单。
        // 必须先通知刷新：onClose 只是收起弹窗、不刷新列表，不通知的话列表里会留着一个
        // 已经 is_deleted=1 的站点，运营还能再点进去对它做第二次映射。
        props.onAbMapped?.();
        props.onClose();
      } else {
        message.error(wt('saveFailed'));
      }
    } catch {
      message.error(wt('saveFailed'));
    }
  };
  const wt = (k: string) => t(`pickup.addressReviewPool.siteForm.${k}`);
  const title =
    props.mode === 'create' ? wt('titleCreate') : props.mode === 'edit' ? wt('titleEdit') : wt('titleView');

  // 每次打开滚回顶部：Modal 默认不销毁内容（destroyOnClose 未开），关闭再打开会复用 DOM，
  // 上次滚到「排期/AB 映射」的位置会被带到下一个站点，运营一打开看到的是表单中段。
  // 用 rAF 等 Modal 内容挂载后再置位——open 变 true 的同一帧容器尚未渲染，此时置 0 无效。
  useEffect(() => {
    if (!props.open) return undefined;
    const id = requestAnimationFrame(() => {
      const scroller = scrollAnchorRef.current?.closest('.ant-modal-body');
      if (scroller) scroller.scrollTop = 0;
    });
    return () => cancelAnimationFrame(id);
  }, [props.open]);

  return (
    <Modal
      title={modalTitle(title)}
      open={props.open}
      width={1280}
      onCancel={props.onClose}
      okText={t('common.confirm', '确认') as string}
      cancelText={(readOnly ? t('common.close', '关闭') : t('common.cancel', '取消')) as string}
      onOk={() => bodyRef.current?.submit()}
      okButtonProps={readOnly ? { style: { display: 'none' } } : undefined}
      // 2.2 需求点2：左下角「AB地址映射」按钮（对齐 PRD 原型）。
      // 只在 showAbButton 时自定义 footer，其余场景保持 antd 默认 footer —— 不改既有形态。
      footer={
        !showAbButton
          ? undefined
          : (originNode) => (
              <div className="flex items-center justify-between">
                <Button icon={<LinkOutlined />} loading={abChecking} onClick={openAbWizard}>
                  {wt('abMappingEntry')}
                </Button>
                <div className="flex items-center gap-2">{originNode}</div>
              </div>
            )
      }
      // 表单字段多、纵向长：加宽到 1080 让排期/AB 映射等多列行不被挤出横向滚动条，可视高度提到 78vh 减少翻滚。
      // overflowX hidden 仅作兜底——真正的溢出源(排期第2列)已按内容实际宽度放开，不靠裁剪掩盖。
      // header/footer 固定并各带分隔线，滚动只发生在两线之间（对齐 PRD 原型）
      styles={{
        header: MODAL_HEADER_STYLE,
        body: modalBodyStyle('78vh'),
        footer: MODAL_FOOTER_STYLE,
      }}
    >
      {/* 滚动锚点：仅用于在打开时定位到 Modal body 滚动容器并滚回顶部 */}
      <div ref={scrollAnchorRef} />
      {/* 整体展开而非逐个列举：此前逐个列举漏掉了 skipStandardize / presetAddressCoreHash /
          presetAddressAnalysisHash 三个 prop，Body 恒取默认值 false，2.1 需求点1「确认无法建联免标准化建站」
          整个功能空转（状态预置生效、拦截照旧、按钮照旧显示，看起来像没改代码）。
          TS 抓不到这类漏传：外壳收下 props 再挑着往下传，少传不报错。展开后新增 prop 自动贯通。
          两个回调放在展开之后：它们要在原回调外再包一层关窗，必须覆盖同名 prop。 */}
      <PickupSiteFormBody
        {...props}
        ref={bodyRef}
        onSaved={(id) => {
          props.onSaved?.(id);
          props.onClose();
        }}
        onSubmit={(v) => {
          props.onSubmit?.(v);
          props.onClose();
        }}
      />
      {/* 2.2 需求点2：与审核池那条路**共用同一个向导**（PRD「交互逻辑：同现有的存量站点的AB映射」）。
          只是 source.kind 不同 —— 向导内部仅在三处取数上分叉，交互、步骤、文案完全一致。 */}
      {showAbButton && abSourceStationId != null && abHubId != null && (
        <AbMappingWizard
          open={abOpen}
          source={{
            kind: 'station',
            id: abSourceStationId,
            hubId: abHubId,
            // 自指判据①：目标 B 不能是本站自己
            selfStationId: abSourceStationId,
            // 判据②(core_hash 比对)刻意不传：表单里拿不到站点的 address_core_hash。
            // 判据①已覆盖「选中自己」这个主场景，且后端有权威的三条自指拦截兜底，
            // 漏判在这里的代价只是运营多走一步被后端拒。
            coreHash: undefined,
            displayAddress: abDisplayAddress,
          }}
          onClose={() => setAbOpen(false)}
          onSubmit={handleAbSubmit}
        />
      )}
    </Modal>
  );
};

export default PickupSiteFormDrawer;
